// src/pages/Plan.js
//
// Chunk 4 — the redesigned Plan page.
//
// Replaces:  Budget.js · Goals.js · Debts.js  (still in repo until chunk 9 cleanup)
//
// Core idea: zero-based monthly planning. Every dollar of expected income
// gets a job — Bills, Debts, Goals, Categories — until "Left to assign"
// hits zero. History chips on every editable line show the last 3 months
// of actual spending so the next allocation is informed by reality, not
// guessed-at limits.
//
// Data model integration:
//   • Income lines    → edit  income_sources.amount     (per month/year)
//   • Bills lines     → edit  bills.amount              (recurring, no per-month rows)
//   • Debt lines      → edit  the auto-created "Debt Payments" budget_category
//                              for THIS month's budget, mirroring how Debts.js
//                              already creates these on insert
//   • Goal lines      → edit  an auto-created "Savings Goals" budget_category
//                              parent + per-goal child for THIS month's budget
//                              (same pattern as Debts.js does for debts)
//   • Category lines  → edit  budget_categories.limit_amount
//   • Parent category → READ-ONLY, computed as sum of children
//
// History chips:
//   • Income          → sum income_sources.amount for that prior month/year
//   • Categories/sub  → sum transactions.amount for that category for that
//                        prior month
//   • Goals/Debts     → sum transactions whose category_id matches the
//                        auto-created budget_category for that prior month
//   • Bills           → no chips (recurring single-amount lines, chips would
//                        repeat the same number)
//
// Past-month read-only:
//   • current month + future months are editable
//   • past months are read-only (matches Budget.js's existing rule, plus
//     editing a past plan retroactively would break the historical record
//     the rail relies on)
//
import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useOutletContext, Link, useNavigate } from 'react-router-dom'
import ReceiptScanner from '../components/ReceiptScanner'
import { supabase } from '../lib/supabase'
import { useDataEvent, emitDataEvent, EVENTS } from '../lib/dataEvents'
import { usePartner } from '../lib/usePartner'
import { theme, atoms, formatMonthYear, firstName } from '../lib/theme'
import { getCurrentCurrency, useCurrency } from '../lib/CurrencyContext'

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────

const FREQUENCY_OPTIONS = [
  { value: 'weekly',    label: 'Weekly' },
  { value: 'biweekly',  label: 'Every 2 weeks' },
  { value: 'monthly',   label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual',    label: 'Annual' },
]

// Bills and income both get an extra "one-time" option for unique
// transactions (one-off gifts, bonuses, refunds, single-charge bills).
const BILL_FREQUENCY_OPTIONS = [
  { value: 'one_time',  label: 'One time' },
  ...FREQUENCY_OPTIONS,
]
const INCOME_FREQUENCY_OPTIONS = [
  ...FREQUENCY_OPTIONS,
  { value: 'semi_monthly', label: 'Bi-monthly (15th & last day)' },
  { value: 'one_time',     label: 'One time' },
]

const INCOME_TYPE_OPTIONS = [
  { value: 'salary',     label: '💼 Salary' },
  { value: 'hourly',     label: '⏱️ Hourly' },
  { value: 'commission', label: '📈 Commission' },
  { value: 'freelance',  label: '💻 Freelance' },
  { value: 'rental',     label: '🏠 Rental' },
  { value: 'investment', label: '📊 Investment' },
  { value: 'other',      label: '💰 Other' },
]

const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

// Reserved category names used as parent buckets for auto-generated lines
const DEBT_BUCKET = 'Debt Payments'
const GOAL_BUCKET = 'Savings Goals'

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

export default function Plan() {
  // Subscribe to currency changes so the page re-renders and the helpers
  // (splitMoney/formatMoney/formatInput) produce fresh-currency strings.
  useCurrency()
  const { partnerId, partnerProfile, loading: partnerLoading } = usePartner()
  const ctx = useOutletContext() || {}
  const isMobile = !!ctx.isMobile
  const navigate = useNavigate()

  // ─── View date (current/future editable, past read-only) ──
  const today = useMemo(() => new Date(), [])
  const [viewDate, setViewDate] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1))
  const month = viewDate.getMonth() + 1
  const year = viewDate.getFullYear()
  const isPastMonth = viewDate < new Date(today.getFullYear(), today.getMonth(), 1)
  // readOnly defaults to true for past months but the user can flip it off
  // via the "Edit historical" toggle in the header.
  const [pastMonthEditingUnlocked, setPastMonthEditingUnlocked] = useState(false)
  const readOnly = isPastMonth && !pastMonthEditingUnlocked

  // Reset unlock when navigating to a different month — every past month
  // starts locked again as a safety net.
  useEffect(() => {
    setPastMonthEditingUnlocked(false)
  }, [month, year])

  // ─── Data state ───────────────────────────────────────────
  const [budget, setBudget] = useState(null)
  const [categories, setCategories] = useState([])
  const [bills, setBills] = useState([])
  const [debts, setDebts] = useState([])
  const [goals, setGoals] = useState([])
  const [incomeSources, setIncomeSources] = useState([])
  const [history, setHistory] = useState({}) // { 'category:<id>': { 'YYYY-MM': sum }, 'income': { 'YYYY-MM': sum } }
  const [railMonths, setRailMonths] = useState([]) // [{ year, month, income, bills, cats, plannedCats, goalsDebts }]
  const [monthSpent, setMonthSpent] = useState(0) // total expense $ for the viewed month — used for past-month "Net" display
  // ─── UI state ─────────────────────────────────────────────
  const [loading, setLoading] = useState(true)
  const [activeForm, setActiveForm] = useState(null) // 'income' | 'bill' | 'debt' | 'goal' | 'category' | { type: 'subcategory', parentId } | null
  const [expandedCats, setExpandedCats] = useState({})
  const [savingId, setSavingId] = useState(null) // id of row currently being saved (debounced from input)
  const [creatingBudget, setCreatingBudget] = useState(false)

  // ─── Effects ──────────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!partnerLoading) fetchAll() }, [partnerId, partnerLoading, month, year])

  // Auto-propose (v2 chunk 4): silently create + pre-fill THIS month's plan the
  // first time it's opened with no budget — the new-month ritual. Future months
  // keep the manual "Create plan" button so browsing ahead never creates a plan
  // by surprise. Idempotent: once a budget exists, createBudgetForViewMonth's
  // own guard makes this a no-op, so a second open just re-renders (no doubling).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const isCurrent = month === today.getMonth() + 1 && year === today.getFullYear()
    if (!loading && !budget && !readOnly && !creatingBudget && isCurrent) {
      createBudgetForViewMonth()
    }
  }, [loading, budget, readOnly, creatingBudget, month, year])

  const fetchAll = async () => {
    setLoading(true)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setLoading(false); return }
    const userIds = [user.id, ...(partnerId ? [partnerId] : [])]

    // Step 1 — find/load budget for this month
    const { data: budgetData } = await supabase
      .from('budgets').select('*')
      .in('user_id', userIds).eq('month', month).eq('year', year).eq('is_active', true)
      .order('created_at').limit(1).maybeSingle()

    setBudget(budgetData || null)

    // Step 2 — load everything else in parallel
    const [
      catsRes,
      billsRes,
      debtsRes,
      goalsRes,
      incomeRes,
      txHistoryRes,
      incomeHistoryRes,
    ] = await Promise.all([
      budgetData
        ? supabase.from('budget_categories').select('*').eq('budget_id', budgetData.id).order('sort_order')
        : Promise.resolve({ data: [] }),
      supabase.from('bills').select('*').in('user_id', userIds).eq('is_active', true).order('due_day'),
      supabase.from('debts').select('*').in('user_id', userIds).order('current_balance', { ascending: false }),
      supabase.from('savings_goals').select('*').in('user_id', userIds).order('created_at'),
      supabase.from('income_sources')
        .select('*').in('user_id', userIds).eq('is_active', true)
        .eq('month', month).eq('year', year).order('created_at'),
      // History: last 3 months of transactions across all categories.
      // We fetch a window covering the 3 prior calendar months and bucket client-side.
      fetchHistoryWindow(userIds, year, month),
      // Income history for the same 3-month window.
      fetchIncomeHistoryWindow(userIds, year, month),
    ])

    setCategories(catsRes.data || [])
    setDebts(debtsRes.data || [])
    setGoals(goalsRes.data || [])
    setIncomeSources(incomeRes.data || [])

    // Build history index keyed BOTH by UUID and by canonical name.
    // UUID keys keep working for any current-budget lookups; canonical name keys
    // ('cat:<parentname>::<catname>') let cross-budget views (e.g. May looking
    // back at January) match historical actuals even though the UUIDs differ
    // per budget. The lookup helper (canonicalKeyForCategory) tries UUID first,
    // then canonical name — see usage sites below.
    //
    // To compute canonical names, we need a UUID → category record map for ALL
    // categories returned by the history query. We pull from a fresh fetch
    // because the history window may include categories from budgets we
    // haven't loaded into `categories` state.
    const allCatsForHistoryRes = await supabase.from('budget_categories')
      .select('id, name, parent_id, budget_id')
      .in('user_id', userIds)
    const allCatsForHistory = allCatsForHistoryRes.data || []
    const catById = {}
    allCatsForHistory.forEach(c => { catById[c.id] = c })
    const canonicalNameForCatId = (catId) => {
      const c = catById[catId]
      if (!c) return null
      const parentName = c.parent_id
        ? ((catById[c.parent_id] || {}).name || '').toLowerCase().trim()
        : ''
      const ownName = (c.name || '').toLowerCase().trim()
      return `cat:${parentName}::${ownName}`
    }

    // ─── Bill → current-budget parent resolution (Bill-Inside-Category Rule) ──
    // Bills are recurring single rows whose category_id points to a SUBCATEGORY
    // in whichever budget it was assigned. Categories are re-created per month
    // with new UUIDs, so we resolve a bill to THIS month's plan by canonical
    // "parentname::subname" and take that subcategory's parent. A bill that does
    // not resolve (null category, or its subcategory isn't in this month's plan)
    // is treated as "needs a category" — surfaced in the Unassigned bills group.
    const curCats = catsRes.data || []
    const curParentById = {}
    curCats.forEach(c => { if (c.parent_id === null) curParentById[c.id] = c })
    const curSubByCanon = {}
    curCats.forEach(c => {
      if (c.parent_id !== null) {
        const pn = (curParentById[c.parent_id]?.name || '').toLowerCase().trim()
        curSubByCanon[`${pn}::${(c.name || '').toLowerCase().trim()}`] = c
      }
    })
    const annotatedBills = (billsRes.data || []).map(b => {
      let _parentId = null, _parentName = null
      if (b.category_id) {
        const bc = catById[b.category_id]
        if (bc && bc.parent_id) {
          const bpn = (catById[bc.parent_id]?.name || '').toLowerCase().trim()
          const curSub = curSubByCanon[`${bpn}::${(bc.name || '').toLowerCase().trim()}`]
          if (curSub) { _parentId = curSub.parent_id; _parentName = curParentById[_parentId]?.name || null }
        } else if (bc && !bc.parent_id) {
          // Legacy: bill assigned directly to a parent — match current parent by name.
          const cn = (bc.name || '').toLowerCase().trim()
          const cp = Object.values(curParentById).find(p => (p.name || '').toLowerCase().trim() === cn)
          if (cp) { _parentId = cp.id; _parentName = cp.name }
        }
      }
      return { ...b, _parentId, _parentName }
    })
    setBills(annotatedBills)

    const historyIdx = {}
    ;(txHistoryRes || []).forEach(tx => {
      if (!tx.category_id) return
      const d = new Date(tx.transaction_date + 'T12:00:00')
      const ym = `${d.getFullYear()}-${d.getMonth() + 1}`
      const amt = parseFloat(tx.amount || 0)

      // 1) UUID-keyed (existing behavior)
      const uuidKey = `category:${tx.category_id}`
      historyIdx[uuidKey] = historyIdx[uuidKey] || {}
      historyIdx[uuidKey][ym] = (historyIdx[uuidKey][ym] || 0) + amt

      // 2) Canonical-name-keyed (new — for cross-budget lookups)
      const canonKey = canonicalNameForCatId(tx.category_id)
      if (canonKey) {
        historyIdx[canonKey] = historyIdx[canonKey] || {}
        historyIdx[canonKey][ym] = (historyIdx[canonKey][ym] || 0) + amt
      }
    })
    // Bucket income history per source-name so each IncomeLine's chips
    // reflect THAT source's history, not the total of all sources.
    ;(incomeHistoryRes || []).forEach(src => {
      const ym = `${src.year}-${src.month}`
      const k = `income:${(src.name || '').toLowerCase().trim()}`
      historyIdx[k] = historyIdx[k] || {}
      historyIdx[k][ym] = (historyIdx[k][ym] || 0) + monthlyEquivalent(src)
    })
    setHistory(historyIdx)

    // Build rail data — last 3 prior months of high-level rollups
    const rail = await buildRailMonths(userIds, year, month)
    setRailMonths(rail)

    // Total spend for the viewed month — only needed for past-month "Net" display,
    // but harmless to compute always (one query, can cache for current month too)
    const monthStart = new Date(year, month - 1, 1).toISOString().split('T')[0]
    const monthEnd = new Date(year, month, 1).toISOString().split('T')[0]
    const { data: monthTxs } = await supabase.from('transactions')
      .select('amount')
      .in('user_id', userIds)
      .eq('type', 'expense')
      .gte('transaction_date', monthStart)
      .lt('transaction_date', monthEnd)
    const totalMonthSpent = (monthTxs || []).reduce((s, t) => s + parseFloat(t.amount || 0), 0)
    setMonthSpent(totalMonthSpent)

    setLoading(false)
  }

  // ─── Helpers: history window queries ─────────────────────
  const fetchHistoryWindow = async (userIds, y, m) => {
    // Cover the 3 calendar months strictly before the current view.
    const start = priorMonthStart(y, m, 3)
    const end = priorMonthStart(y, m, 0)
    const { data } = await supabase.from('transactions')
      .select('id, amount, category_id, transaction_date, type')
      .in('user_id', userIds)
      .eq('type', 'expense')
      .gte('transaction_date', start.toISOString().split('T')[0])
      .lt('transaction_date', end.toISOString().split('T')[0])
    return data || []
  }

  const fetchIncomeHistoryWindow = async (userIds, y, m) => {
    // 3 prior month/year combos
    const targets = []
    for (let i = 1; i <= 3; i++) {
      const dt = new Date(y, m - 1 - i, 1)
      targets.push({ y: dt.getFullYear(), m: dt.getMonth() + 1 })
    }
    const { data } = await supabase.from('income_sources')
      .select('name, amount, frequency, month, year, is_active')
      .in('user_id', userIds)
      .or(targets.map(t => `and(month.eq.${t.m},year.eq.${t.y})`).join(','))
    return (data || []).filter(s => s.is_active !== false)
  }

  const buildRailMonths = async (userIds, y, m) => {
    const out = []
    for (let i = 1; i <= 3; i++) {
      const dt = new Date(y, m - 1 - i, 1)
      const ry = dt.getFullYear()
      const rm = dt.getMonth() + 1
      const monthStart = new Date(ry, rm - 1, 1).toISOString().split('T')[0]
      const monthEnd = new Date(ry, rm, 1).toISOString().split('T')[0]

      const [incomeRes, txRes, budgetRes] = await Promise.all([
        supabase.from('income_sources')
          .select('amount, frequency').in('user_id', userIds)
          .eq('is_active', true).eq('month', rm).eq('year', ry),
        supabase.from('transactions')
          .select('amount, category_id, type').in('user_id', userIds)
          .eq('type', 'expense')
          .gte('transaction_date', monthStart).lt('transaction_date', monthEnd),
        supabase.from('budgets').select('id, total_amount').in('user_id', userIds)
          .eq('is_active', true).eq('month', rm).eq('year', ry).limit(1).maybeSingle(),
      ])

      const income = (incomeRes.data || []).reduce((s, src) => s + monthlyEquivalent(src), 0)
      const allTx = txRes.data || []

      // Look up that month's categories to know which were debts/goals (bucket parents)
      let debtCatIds = new Set()
      let goalCatIds = new Set()
      let regularSpend = 0
      let goalsDebtsSpend = 0
      let plannedCats = 0

      if (budgetRes.data) {
        const { data: cats } = await supabase.from('budget_categories')
          .select('id, name, parent_id, limit_amount').eq('budget_id', budgetRes.data.id)
        const debtParent = (cats || []).find(c => c.name === DEBT_BUCKET && !c.parent_id)
        const goalParent = (cats || []).find(c => c.name === GOAL_BUCKET && !c.parent_id)
        if (debtParent) {
          debtCatIds.add(debtParent.id)
          ;(cats || []).filter(c => c.parent_id === debtParent.id).forEach(c => debtCatIds.add(c.id))
        }
        if (goalParent) {
          goalCatIds.add(goalParent.id)
          ;(cats || []).filter(c => c.parent_id === goalParent.id).forEach(c => goalCatIds.add(c.id))
        }
        // Planned categories total — exclude debt/goal buckets, only leaf-level
        plannedCats = (cats || [])
          .filter(c => !debtCatIds.has(c.id) && !goalCatIds.has(c.id))
          .reduce((s, c) => s + parseFloat(c.limit_amount || 0), 0)
      }

      allTx.forEach(tx => {
        const amt = parseFloat(tx.amount || 0)
        if (debtCatIds.has(tx.category_id) || goalCatIds.has(tx.category_id)) {
          goalsDebtsSpend += amt
        } else {
          regularSpend += amt
        }
      })

      const billsTotal = bills.reduce((s, b) => s + parseFloat(b.amount || 0), 0) // approximation: current bills
      const result = income - billsTotal - regularSpend - goalsDebtsSpend
      out.push({
        year: ry,
        month: rm,
        income,
        bills: billsTotal,
        cats: regularSpend,
        plannedCats,
        goalsDebts: goalsDebtsSpend,
        result,
      })
    }
    return out
  }

  // ─── Helpers: monthly equivalent for variable-frequency income ──
  function monthlyEquivalent(source) {
    const a = parseFloat(source.amount || 0)
    switch (source.frequency) {
      case 'weekly':       return a * 4.33
      case 'biweekly':     return a * 2.17
      case 'semi_monthly': return a * 2  // 2 paychecks per month (15th & last)
      case 'quarterly':    return a / 3
      case 'annual':       return a / 12
      case 'one_time':     return a  // single payment that month — full amount
      default:             return a
    }
  }

  function priorMonthStart(y, m, monthsBack) {
    return new Date(y, m - 1 - monthsBack, 1)
  }

  // Look up a category's history entry. Tries UUID first (handles current
  // budget), then falls back to canonical name (handles cross-budget months).
  // `cat` is a budget_categories record with id, name, parent_id.
  function getCategoryHistory(cat) {
    if (!cat) return {}

    // Helper: get history for a single category (UUID first, canonical name fallback)
    const lookupOne = (c) => {
      const byUuid = history[`category:${c.id}`]
      if (byUuid && Object.keys(byUuid).length > 0) return byUuid
      const parent = c.parent_id ? categories.find(p => p.id === c.parent_id) : null
      const parentName = parent ? (parent.name || '').toLowerCase().trim() : ''
      const ownName = (c.name || '').toLowerCase().trim()
      return history[`cat:${parentName}::${ownName}`] || {}
    }

    // Leaf categories (subcategories): just their own history
    if (cat.parent_id) return lookupOne(cat)

    // Parent categories: roll up own + all children's history (matches how
    // Reports computes it — most transactions live on subcategories, so the
    // parent's direct history is usually empty).
    const own = lookupOne(cat)
    const subs = categories.filter(c => c.parent_id === cat.id)
    const merged = { ...own }
    subs.forEach(sub => {
      const subHist = lookupOne(sub)
      Object.entries(subHist).forEach(([ym, amt]) => {
        merged[ym] = (merged[ym] || 0) + amt
      })
    })
    return merged
  }

  // ─── Auto-create a budget for current/future month if missing ──
  const createBudgetForViewMonth = async () => {
    if (creatingBudget || readOnly || budget) return
    setCreatingBudget(true)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setCreatingBudget(false); return }
    const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December']
    const userIds = [user.id, ...(partnerId ? [partnerId] : [])]

    // Insert the new budget first
    const { data: created } = await supabase.from('budgets').insert({
      user_id: user.id, month, year, is_active: true, total_amount: 0,
      name: `${monthNames[month - 1]} ${year}`,
    }).select().single()
    if (!created) { setCreatingBudget(false); return }
    emitDataEvent(EVENTS.BUDGET_SAVED)

    // Try to copy categories from the most recent prior budget
    const { data: priorBudgets } = await supabase.from('budgets')
      .select('id, year, month')
      .in('user_id', userIds)
      .eq('is_active', true)
      .neq('id', created.id)
      .or(`year.lt.${year},and(year.eq.${year},month.lt.${month})`)
      .order('year', { ascending: false })
      .order('month', { ascending: false })
      .limit(1)

    if (priorBudgets && priorBudgets.length > 0) {
      // Copy parents first, capture id mapping (oldId → newId), then copy subs
      const priorBudgetId = priorBudgets[0].id
      const { data: priorCats } = await supabase.from('budget_categories')
        .select('*')
        .eq('budget_id', priorBudgetId)
        .order('sort_order')

      if (priorCats && priorCats.length > 0) {
        const parents = priorCats.filter(c => !c.parent_id)
        const subs = priorCats.filter(c => c.parent_id)
        const oldToNewParentId = {}

        for (const p of parents) {
          const { data: newP } = await supabase.from('budget_categories').insert({
            user_id: user.id,
            budget_id: created.id,
            name: p.name,
            icon: p.icon,
            limit_amount: parseFloat(p.limit_amount || 0),
            sort_order: p.sort_order,
            parent_id: null,
          }).select().single()
          if (newP) oldToNewParentId[p.id] = newP.id
        }

        if (subs.length > 0) {
          const subInserts = subs.map(s => ({
            user_id: user.id,
            budget_id: created.id,
            name: s.name,
            icon: s.icon,
            limit_amount: parseFloat(s.limit_amount || 0),
            sort_order: s.sort_order,
            parent_id: oldToNewParentId[s.parent_id] || null,
          }))
          await supabase.from('budget_categories').insert(subInserts)
        }
        emitDataEvent(EVENTS.CATEGORY_SAVED)
      }
    } else {
      // No prior budget — fall back to default seed (only for the current month)
      const isCurrentMonth = month === today.getMonth() + 1 && year === today.getFullYear()
      if (isCurrentMonth) {
        await supabase.rpc('seed_categories_for_budget', {
          p_user_id: user.id,
          p_budget_id: created.id,
        })
        emitDataEvent(EVENTS.CATEGORY_SAVED)
      }
    }

    // ─── Auto-propose (v2 chunk 4): carry income + reconcile debt/goal lines ──
    // The category copy above already carried user categories AND the Debt
    // Payments / Savings Goals bucket child lines (they're just budget_categories).
    // Here we (a) carry income sources forward — income is per-month, so the copy
    // never touched it — and (b) re-derive debt/goal lines from the CURRENT debts
    // and savings_goals tables so new/changed debts and goals are reflected and
    // reached goals get zeroed. Extends the existing behavior; does not replace it.
    if (priorBudgets && priorBudgets.length > 0) {
      const priorY = priorBudgets[0].year
      const priorM = priorBudgets[0].month
      // Idempotency: only seed income if this month has none yet.
      const { data: existingIncome } = await supabase.from('income_sources')
        .select('id').in('user_id', userIds)
        .eq('month', month).eq('year', year).eq('is_active', true).limit(1)
      if (!existingIncome || existingIncome.length === 0) {
        const { data: priorIncome } = await supabase.from('income_sources')
          .select('name, amount, frequency, source_type, is_variable')
          .in('user_id', userIds)
          .eq('month', priorM).eq('year', priorY).eq('is_active', true)
        if (priorIncome && priorIncome.length > 0) {
          await supabase.from('income_sources').insert(priorIncome.map(s => ({
            user_id: user.id,
            name: s.name,
            amount: s.amount,
            frequency: s.frequency,
            source_type: s.source_type,
            is_variable: s.is_variable,
            is_active: true,
            month, year,
          })))
          emitDataEvent(EVENTS.INCOME_SAVED)
        }
      }
    }

    await reconcileDebtGoalLines(created.id, user.id, userIds)

    await fetchAll()
    setCreatingBudget(false)
  }

  // ─── Reconcile the Debt Payments / Savings Goals bucket lines for a budget ──
  // Ensures every active debt and active goal has a child line in the new budget.
  // The generic category copy above already carried forward last month's debt and
  // goal lines (including any "extra toward debt" — spec §11); this only CREATES
  // lines for debts/goals that don't yet have one (e.g. added since last budget),
  // and zeroes goals that have hit their target (the "freed up" case; the freed
  // money then shows in Left to assign). Extends the DebtForm/GoalForm creation
  // pattern to monthly rollover. Idempotent: carried lines are left untouched.
  const reconcileDebtGoalLines = async (budgetId, userId, userIds) => {
    const { data: newCatsRaw } = await supabase.from('budget_categories')
      .select('id, name, parent_id, limit_amount').eq('budget_id', budgetId)
    const newCats = newCatsRaw || []
    let dBucket = newCats.find(x => x.name === DEBT_BUCKET && !x.parent_id)
    let gBucket = newCats.find(x => x.name === GOAL_BUCKET && !x.parent_id)

    // Debts — create a minimum-payment line for any active debt that lacks one.
    // Carried lines (incl. extra-toward-debt) are preserved as proposed.
    const { data: curDebts } = await supabase.from('debts').select('*').in('user_id', userIds)
    for (const d of (curDebts || [])) {
      const pay = parseFloat(d.monthly_payment || 0)
      if (!(pay > 0)) continue
      const line = dBucket ? newCats.find(x => x.parent_id === dBucket.id && x.name === d.name) : null
      if (!line) {
        if (!dBucket) {
          const { data: nb } = await supabase.from('budget_categories').insert({
            user_id: userId, budget_id: budgetId, name: DEBT_BUCKET, icon: '💳',
            limit_amount: 0, sort_order: 999,
          }).select().single()
          if (nb) { dBucket = nb; newCats.push(nb) }
        }
        if (dBucket) {
          const { data: nl } = await supabase.from('budget_categories').insert({
            user_id: userId, budget_id: budgetId, name: d.name, icon: '💳',
            limit_amount: pay, parent_id: dBucket.id, sort_order: 999,
          }).select().single()
          if (nl) newCats.push(nl)
        }
      }
    }

    // Goals — carry forward partial goals, zero goals that have hit their target.
    const { data: curGoals } = await supabase.from('savings_goals').select('*').in('user_id', userIds)
    for (const g of (curGoals || [])) {
      const target = parseFloat(g.target_amount || 0)
      const current = parseFloat(g.current_amount || 0)
      const reached = target > 0 && current >= target
      const line = gBucket ? newCats.find(x => x.parent_id === gBucket.id && x.name === g.name) : null
      if (line) {
        // Reached goal: drop the carried allocation to $0 so it's freed to redirect.
        // Partial goal: leave the carried limit_amount untouched (carry forward).
        if (reached && parseFloat(line.limit_amount || 0) > 0.005) {
          await supabase.from('budget_categories').update({ limit_amount: 0 }).eq('id', line.id)
        }
      } else {
        if (!gBucket) {
          const { data: nb } = await supabase.from('budget_categories').insert({
            user_id: userId, budget_id: budgetId, name: GOAL_BUCKET, icon: '🎯',
            limit_amount: 0, sort_order: 998,
          }).select().single()
          if (nb) { gBucket = nb; newCats.push(nb) }
        }
        if (gBucket) {
          const seed = reached ? 0 : parseFloat(g.monthly_contribution || 0)
          const { data: nl } = await supabase.from('budget_categories').insert({
            user_id: userId, budget_id: budgetId, name: g.name, icon: '🎯',
            limit_amount: seed, parent_id: gBucket.id, sort_order: 998,
          }).select().single()
          if (nl) newCats.push(nl)
        }
      }
    }
    emitDataEvent(EVENTS.CATEGORY_SAVED)
  }

  // ─── Derived: parents/children + roll-up math ────────────
  const parentCategories = useMemo(
    () => categories.filter(c => !c.parent_id),
    [categories]
  )
  const getSubcategories = (parentId) => categories.filter(c => c.parent_id === parentId)

  const getRollupLimit = (cat) => {
    const subs = getSubcategories(cat.id)
    if (subs.length === 0) return parseFloat(cat.limit_amount || 0)
    return subs.reduce((s, x) => s + parseFloat(x.limit_amount || 0), 0)
  }

  // Identify "system" buckets — Debt Payments, Savings Goals
  const debtBucket  = parentCategories.find(c => c.name === DEBT_BUCKET)
  const goalBucket  = parentCategories.find(c => c.name === GOAL_BUCKET)
  const userParents = parentCategories.filter(c => c !== debtBucket && c !== goalBucket)

  // For each goal/debt, find its child budget_category (auto-created)
  const findGoalLine = (goal) => {
    if (!goalBucket) return null
    return categories.find(c => c.parent_id === goalBucket.id && c.name === goal.name)
  }
  const findDebtLine = (debt) => {
    if (!debtBucket) return null
    return categories.find(c => c.parent_id === debtBucket.id && c.name === debt.name)
  }

  // ─── Totals for ledger header ────────────────────────────
  const expectedIncome = incomeSources.reduce((s, src) => s + monthlyEquivalent(src), 0)

  const billsTotal = bills.reduce((s, b) => s + parseFloat(b.amount || 0), 0)

  const debtsTotal = debts.reduce((s, d) => {
    const line = findDebtLine(d)
    return s + (line ? parseFloat(line.limit_amount || 0) : parseFloat(d.monthly_payment || 0))
  }, 0)

  const goalsTotal = goals.reduce((s, g) => {
    const line = findGoalLine(g)
    return s + (line ? parseFloat(line.limit_amount || 0) : 0)
  }, 0)

  const categoriesTotal = userParents.reduce((s, c) => s + getRollupLimit(c), 0)

  const allocated = billsTotal + debtsTotal + goalsTotal + categoriesTotal
  const leftToAssign = expectedIncome - allocated
  const balanced = Math.abs(leftToAssign) < 0.5 // round-off tolerance
  const overAllocated = leftToAssign < -0.5

  // ─── Bills grouped by their resolved current-budget parent (Chunk 3) ──────
  // billsTotal above is unchanged (Model A: bills stay an additive Zero-Rule
  // segment, NOT folded into category allocations). This grouping is purely for
  // rendering bills inside their parent category and surfacing the per-category
  // "$X in bills" math.
  const billsByParent = {}
  const uncategorizedBills = []
  bills.forEach(b => {
    if (b._parentId) (billsByParent[b._parentId] = billsByParent[b._parentId] || []).push(b)
    else uncategorizedBills.push(b)
  })
  const uncategorizedBillCount = uncategorizedBills.length
  // Current-budget subcategory options for the inline "assign a category" picker.
  const subCategoryOptions = categories
    .filter(c => c.parent_id !== null)
    .map(sub => ({
      id: sub.id,
      label: `${(parentCategories.find(p => p.id === sub.parent_id) || {}).name || '—'} · ${sub.name}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

  // ─── Bill row handlers (shared by in-category rows + Unassigned group) ────
  const saveBillAmount = async (bill, newAmount) => {
    setSavingId(bill.id)
    await supabase.from('bills').update({ amount: newAmount }).eq('id', bill.id)
    emitDataEvent(EVENTS.BILL_UPDATED)
    await fetchAll()
    setSavingId(null)
  }
  const deleteBill = async (bill) => {
    if (!window.confirm(`Remove "${bill.name}"?`)) return
    await supabase.from('bills').update({ is_active: false }).eq('id', bill.id)
    emitDataEvent(EVENTS.BILL_DELETED)
    await fetchAll()
  }
  const assignBillCategory = async (bill, subId) => {
    if (!subId) return
    setSavingId(bill.id)
    await supabase.from('bills').update({ category_id: subId }).eq('id', bill.id)
    emitDataEvent(EVENTS.BILL_UPDATED)
    await fetchAll()
    setSavingId(null)
  }

  // ─── Lock in / unlock this month's plan (v2 chunk 4) ─────────
  // Locking stamps budgets.locked_at; unlocking clears it. Either partner can do
  // either (Partner Symmetry Rule) — the lock state lives on the shared budget row.
  const [lockBusy, setLockBusy] = useState(false)
  const locked = !!budget?.locked_at
  const lockPlan = async () => {
    if (!budget || lockBusy) return
    setLockBusy(true)
    await supabase.from('budgets').update({ locked_at: new Date().toISOString() }).eq('id', budget.id)
    emitDataEvent(EVENTS.BUDGET_SAVED)
    await fetchAll()
    setLockBusy(false)
  }
  const unlockPlan = async () => {
    if (!budget || lockBusy) return
    setLockBusy(true)
    await supabase.from('budgets').update({ locked_at: null }).eq('id', budget.id)
    emitDataEvent(EVENTS.BUDGET_SAVED)
    await fetchAll()
    setLockBusy(false)
  }

  // Reached goals whose allocation was freed (limit_amount ~0) surface a note.
  // freed amount = the goal's standard monthly_contribution (what's now redirectable).
  const freedGoals = goals
    .filter(g => {
      const t = parseFloat(g.target_amount || 0)
      const cur = parseFloat(g.current_amount || 0)
      if (!(t > 0 && cur >= t)) return false
      const line = findGoalLine(g)
      return !line || parseFloat(line.limit_amount || 0) < 0.005
    })
    .map(g => ({ name: g.name, amount: parseFloat(g.monthly_contribution || 0) }))

  // Flow bar percentages (of expectedIncome)
  const safeIncome = expectedIncome > 0 ? expectedIncome : 1
  const flowSegments = [
    { kind: 'bills', amount: billsTotal,        pct: (billsTotal       / safeIncome) * 100 },
    { kind: 'debts', amount: debtsTotal,        pct: (debtsTotal       / safeIncome) * 100 },
    { kind: 'goals', amount: goalsTotal,        pct: (goalsTotal       / safeIncome) * 100 },
    { kind: 'cats',  amount: categoriesTotal,   pct: (categoriesTotal  / safeIncome) * 100 },
    { kind: 'left',  amount: Math.max(0, leftToAssign),
                                   pct: Math.max(0, (leftToAssign / safeIncome) * 100) },
  ]

  // ─── Month nav ───────────────────────────────────────────
  const stepMonth = (delta) => {
    const next = new Date(viewDate)
    next.setMonth(next.getMonth() + delta)
    setViewDate(next)
  }

  // ─── Render: loading ──────────────────────────────────────
  if (loading) {
    return (
      <div style={styles.loading}>
        <span>Loading your plan…</span>
      </div>
    )
  }

  // ─── Render: empty state if no budget for editable month ──
  if (!budget && !readOnly) {
    return (
      <div style={isMobile ? styles.shellMobile : styles.shell}>
        <div style={isMobile ? styles.mainMobile : styles.main}>
          <PageHeader
            month={month} year={year}
            partnerProfile={partnerProfile}
            onPrev={() => stepMonth(-1)} onNext={() => stepMonth(1)}
            isMobile={isMobile}
          />
          <div style={styles.emptyBudget}>
            <h2 style={styles.emptyBudgetTitle}>No plan yet for {formatMonthYear(viewDate)}</h2>
            <p style={styles.emptyBudgetText}>
              Set up this month's plan to start giving every dollar a job.
              We'll seed it from your income, bills, debts, and goals.
            </p>
            <button
              type="button"
              style={styles.emptyBudgetCta}
              onClick={createBudgetForViewMonth}
              disabled={creatingBudget}
            >
              {creatingBudget ? 'Creating…' : 'Create plan'}
            </button>
          </div>
        </div>
      </div>
    )
  }

 // ─── Render: empty state for past month with no budget ────
  if (!budget && readOnly) {
    return (
      <div style={isMobile ? styles.shellMobile : styles.shell}>
        <div style={isMobile ? styles.mainMobile : styles.main}>
          <PageHeader
            month={month} year={year}
            partnerProfile={partnerProfile}
            onPrev={() => stepMonth(-1)} onNext={() => stepMonth(1)}
            isMobile={isMobile}
          />
          <div style={styles.emptyBudget}>
            <h2 style={styles.emptyBudgetTitle}>No plan recorded for {formatMonthYear(viewDate)}</h2>
            <p style={styles.emptyBudgetText}>
              Past months are read-only by default. Unlock to backfill a plan, income, bills, or transactions.
            </p>
            <button
              type="button"
              style={styles.emptyBudgetCta}
              onClick={() => setPastMonthEditingUnlocked(true)}
            >
              Unlock historical editing
            </button>
          </div>
        </div>
      </div>
    )
  }
  // ─── Render: full plan ────────────────────────────────────
  return (
    <div style={isMobile ? styles.shellMobile : styles.shell}>

      {/* Main column */}
      <div style={isMobile ? styles.mainMobile : styles.main}>

        <PageHeader
          month={month} year={year}
          partnerProfile={partnerProfile}
          onPrev={() => stepMonth(-1)} onNext={() => stepMonth(1)}
          readOnly={readOnly}
          locked={locked}
          isMobile={isMobile}
        />

        {/* Past-month editing toggle banner */}
        {isPastMonth && (
          <div style={styles.pastMonthBanner}>
            <span style={styles.pastMonthBannerText}>
              {pastMonthEditingUnlocked
                ? `Editing ${formatMonthYear(viewDate)} — changes will affect historical data.`
                : `${formatMonthYear(viewDate)} is locked to prevent accidental edits.`}
            </span>
            <button
              type="button"
              style={pastMonthEditingUnlocked ? styles.pastMonthBannerLockBtn : styles.pastMonthBannerUnlockBtn}
              onClick={() => setPastMonthEditingUnlocked(u => !u)}
            >
              {pastMonthEditingUnlocked ? 'Lock' : 'Unlock to edit'}
            </button>
          </div>
        )}

        {/* Ledger: forward-looking for current/future, historical for past */}
        <Ledger
          expectedIncome={expectedIncome}
          allocated={allocated}
          leftToAssign={leftToAssign}
          balanced={balanced}
          overAllocated={overAllocated}
          isMobile={isMobile}
          isPastMonth={isPastMonth && !pastMonthEditingUnlocked}
          monthSpent={monthSpent}
        />

        {/* Flow bar + legend */}
        {expectedIncome > 0 && (
          <FlowBar segments={flowSegments} isMobile={isMobile} />
        )}

        {/* Plan body */}
        <div style={styles.planBody}>

          {/* INCOME */}
          <Section
            iconKind="income"
            title="Income"
            subtotalLabel="expected this month"
            subtotalAmount={expectedIncome}
            actionLabel={readOnly ? null : '+ Add income'}
            onAction={() => setActiveForm('income')}
          >
            {incomeSources.length === 0 && (
              <EmptyLine
                text="No income for this month"
                cta={readOnly ? null : 'Add income source'}
                onCtaClick={() => setActiveForm('income')}
              />
            )}
            {incomeSources.map(src => (
              <IncomeLine
                key={src.id}
                source={src}
                history={history[`income:${(src.name || '').toLowerCase().trim()}`] || {}}
                viewYear={year}
                viewMonth={month}
                readOnly={readOnly}
                saving={savingId === src.id}
                onSave={async (newAmount) => {
                  setSavingId(src.id)
                  await supabase.from('income_sources')
                    .update({ amount: newAmount }).eq('id', src.id)
                  await fetchAll()
                  setSavingId(null)
                }}
                onEdit={() => setActiveForm({ type: 'income-edit', source: src })}
              />
            ))}
          </Section>

          {/* Bills are no longer a top-level section — they render inside their
              parent category in Spending Categories (Bill-Inside-Category Rule).
              They still count as their own additive segment in the Zero Rule /
              proportional bar (billsTotal, above). */}

          {/* DEBTS — allocation amounts editable here; debt CRUD lives on the
              Debts page (Chunk 6). "Manage debts →" navigates there. */}
          {debts.length > 0 && (
            <Section
              iconKind="debts"
              title="Debt Payments"
              subtotalLabel={`claims · ${expectedIncome > 0 ? Math.round(debtsTotal / expectedIncome * 100) : 0}% of income`}
              subtotalAmount={debtsTotal}
              actionLabel={readOnly ? null : 'Manage debts →'}
              onAction={() => navigate('/debts')}
            >
              {debts.map(d => {
                const line = findDebtLine(d)
                const lineId = line?.id
                return (
                  <DebtLine
                    key={d.id}
                    debt={d}
                    line={line}
                    history={getCategoryHistory(line)}
                    viewYear={year}
                    viewMonth={month}
                    readOnly={readOnly || !line}
                    saving={savingId === lineId}
                    onSave={async (newAmount) => {
                      if (!line) return
                      setSavingId(lineId)
                      await supabase.from('budget_categories')
                        .update({ limit_amount: newAmount }).eq('id', lineId)
                      await fetchAll()
                      setSavingId(null)
                    }}
                  />
                )
              })}
            </Section>
          )}
          {debts.length === 0 && !readOnly && (
            <Section
              iconKind="debts"
              title="Debt Payments"
              subtotalLabel="none tracked"
              subtotalAmount={0}
              actionLabel="Manage debts →"
              onAction={() => navigate('/debts')}
            >
              <EmptyLine
                text="No debts tracked"
                cta="Manage debts →"
                onCtaClick={() => navigate('/debts')}
              />
            </Section>
          )}

          {/* GOALS */}
          {goals.length > 0 && (
            <Section
              iconKind="goals"
              title="Savings Goals"
              subtotalLabel={`claims · ${expectedIncome > 0 ? Math.round(goalsTotal / expectedIncome * 100) : 0}% of income`}
              subtotalAmount={goalsTotal}
              actionLabel={readOnly ? null : '+ Add goal'}
              onAction={() => setActiveForm('goal')}
            >
              {/* Reached-goal notes (v2 chunk 4): a goal that hit its target keeps
                  its allocation at $0 and frees that money to redirect. No-Surprise
                  Rule — the user sees exactly what was freed. */}
              {!readOnly && freedGoals.map(fg => (
                <div key={`freed-${fg.name}`} style={styles.freedGoalNote}>
                  <span style={styles.freedGoalDot} aria-hidden="true" />
                  <span>
                    <strong style={{ fontWeight: 600 }}>{fg.name}</strong> reached
                    {fg.amount > 0.005
                      ? <> — {formatMoney(fg.amount)} freed up to redirect</>
                      : <> — freed up to redirect</>}
                  </span>
                </div>
              ))}
              {goals.map(g => {
                const line = findGoalLine(g)
                const lineId = line?.id
                return (
                  <GoalLine
                    key={g.id}
                    goal={g}
                    line={line}
                    history={getCategoryHistory(line)}
                    viewYear={year}
                    viewMonth={month}
                    readOnly={readOnly || !line}
                    saving={savingId === lineId}
                    onSave={async (newAmount) => {
                      if (!line) return
                      setSavingId(lineId)
                      await supabase.from('budget_categories')
                        .update({ limit_amount: newAmount }).eq('id', lineId)
                      await fetchAll()
                      setSavingId(null)
                    }}
                    onEdit={() => setActiveForm({ type: 'goal-edit', goal: g })}
                    onDelete={async () => {
                      if (!window.confirm(`Remove goal "${g.name}"? This cannot be undone.`)) return
                      await supabase.from('savings_goals').delete().eq('id', g.id)
                      await fetchAll()
                    }}
                  />
                )
              })}
            </Section>
          )}
          {goals.length === 0 && !readOnly && (
            <Section
              iconKind="goals"
              title="Savings Goals"
              subtotalLabel="none tracked"
              subtotalAmount={0}
              actionLabel="+ Add goal"
              onAction={() => setActiveForm('goal')}
            >
              <EmptyLine
                text="No savings goals yet"
                cta="Add a goal"
                onCtaClick={() => setActiveForm('goal')}
              />
            </Section>
          )}

          {/* SPENDING CATEGORIES */}
          <Section
            iconKind="cats"
            title="Spending Categories"
            subtotalLabel={`allocated · ${expectedIncome > 0 ? Math.round(categoriesTotal / expectedIncome * 100) : 0}% of income`}
            subtotalAmount={categoriesTotal}
            actionLabel={readOnly ? null : '+ Add category'}
            onAction={() => setActiveForm('category')}
            secondaryActionLabel={readOnly ? null : '+ Add bill'}
            onSecondaryAction={() => setActiveForm('bill')}
          >
            {/* Unassigned bills — bills with no category in this month's plan.
                Each shows an inline picker; selecting writes category_id and
                re-renders the bill into its category. Blocks lock-in (footer). */}
            {uncategorizedBills.length > 0 && (
              <UnassignedBills
                bills={uncategorizedBills}
                subOptions={subCategoryOptions}
                readOnly={readOnly}
                savingId={savingId}
                onAssign={assignBillCategory}
                onBillSave={saveBillAmount}
                onEditBill={(bill) => setActiveForm({ type: 'bill-edit', bill })}
                onDeleteBill={deleteBill}
              />
            )}
            {userParents.length === 0 && (
              <EmptyLine
                text="No spending categories yet"
                cta={readOnly ? null : 'Add a category'}
                onCtaClick={() => setActiveForm('category')}
              />
            )}
            {userParents.map(parent => {
              const subs = getSubcategories(parent.id)
              const isExpanded = expandedCats[parent.id] ?? false
              const rollup = getRollupLimit(parent)
              return (
                <CategoryGroup
                  key={parent.id}
                  parent={parent}
                  subs={subs}
                  bills={billsByParent[parent.id] || []}
                  rollup={rollup}
                  history={getCategoryHistory(parent)}
                  isExpanded={isExpanded}
                  onToggle={() => setExpandedCats(s => ({ ...s, [parent.id]: !isExpanded }))}
                  viewYear={year}
                  viewMonth={month}
                  readOnly={readOnly}
                  savingId={savingId}
                  history_byCat={history}
                  onBillSave={saveBillAmount}
                  onEditBill={(bill) => setActiveForm({ type: 'bill-edit', bill })}
                  onDeleteBill={deleteBill}
                  onAddBill={() => setActiveForm({ type: 'bill', presetCategoryId: subs[0]?.id || null })}
                  onSubSave={async (sub, newAmount) => {
                    setSavingId(sub.id)
                    await supabase.from('budget_categories')
                      .update({ limit_amount: newAmount }).eq('id', sub.id)
                    await fetchAll()
                    setSavingId(null)
                  }}
                  onParentSave={async (newAmount) => {
                    setSavingId(parent.id)
                    await supabase.from('budget_categories')
                      .update({ limit_amount: newAmount }).eq('id', parent.id)
                    await fetchAll()
                    setSavingId(null)
                  }}
                  onAddSub={() => setActiveForm({ type: 'subcategory', parentId: parent.id })}
                  onEditCat={(cat) => setActiveForm({ type: 'category-edit', cat })}
                  onDeleteCat={async (cat) => {
                    const subs = getSubcategories(cat.id)
                    const msg = subs.length > 0
                      ? `Delete "${cat.name}" and its ${subs.length} subcategor${subs.length === 1 ? 'y' : 'ies'}?`
                      : `Delete "${cat.name}"?`
                    if (!window.confirm(msg)) return
                    if (subs.length > 0) {
                      await supabase.from('budget_categories').delete().in('id', subs.map(s => s.id))
                    }
                    await supabase.from('budget_categories').delete().eq('id', cat.id)
                    emitDataEvent(EVENTS.CATEGORY_DELETED)
                    fetchAll()
                  }}
                />
              )
            })}
          </Section>

          {/* Balance summary — only for current/future months. Past months show net in Ledger above. */}
          {!readOnly && (
            <BalanceSummary
              leftToAssign={leftToAssign}
              balanced={balanced}
              overAllocated={overAllocated}
              uncategorizedBillCount={uncategorizedBillCount}
              locked={locked}
              lockedAt={budget?.locked_at}
              lockBusy={lockBusy}
              onLock={lockPlan}
              onUnlock={unlockPlan}
              firstGoal={goals[0]}
              firstGoalLineId={goals[0] ? findGoalLine(goals[0])?.id : null}
              onAutoAssign={async () => {
                if (!goals[0] || leftToAssign <= 0) return
                const goalLine = findGoalLine(goals[0])
                if (!goalLine) return
                const newAmount = parseFloat(goalLine.limit_amount || 0) + leftToAssign
                setSavingId(goalLine.id)
                await supabase.from('budget_categories')
                  .update({ limit_amount: newAmount }).eq('id', goalLine.id)
                await fetchAll()
                setSavingId(null)
              }}
            />
          )}

        </div>
      </div>

      {/* Right rail — desktop only */}
      {!isMobile && (
        <aside style={styles.rail}>
          <div style={styles.railHead}>
            <h3 style={styles.railTitle}>Looking back</h3>
            <p style={styles.railSub}>
              Whole-month rollups for context. Each line above also shows its own three-month history inline.
            </p>
          </div>

          {railMonths.map(rm => {
            const isUnder = rm.result >= 0
            return (
              <div key={`${rm.year}-${rm.month}`} style={styles.railMonth}>
                <div style={styles.railMonthHead}>
                  <span style={styles.railMonthName}>
                    {SHORT_MONTHS[rm.month - 1]} {rm.year}
                  </span>
                  <span style={{
                    ...styles.railMonthResult,
                    color: isUnder ? theme.colors.green : theme.colors.rose,
                  }}>
                    {isUnder ? '+' : '−'}{formatMoney(Math.abs(rm.result))}
                    {isUnder ? ' saved' : ' over'}
                  </span>
                </div>
                <RailRow label="Income earned" value={formatMoney(rm.income)} />
                <RailRow label="Bills paid" value={formatMoney(rm.bills)} />
                <RailRow
                  label="Categories"
                  value={
                    <>
                      {formatMoney(rm.cats)}
                      {rm.plannedCats > 0 && (
                        <span style={styles.railPlanned}> / {formatMoney(rm.plannedCats)} plan</span>
                      )}
                    </>
                  }
                />
                <RailRow label="Goals + debt" value={formatMoney(rm.goalsDebts)} />
              </div>
            )
          })}

          <Link to="/log" style={styles.railLink}>See full history →</Link>

          <div style={styles.railDivider} />

          <div style={styles.railTip}>
            <strong style={{ color: theme.colors.purple, fontWeight: 600 }}>How chips work.</strong>
            {' '}Each line shows the last 3 months of actual spending. Click a chip's
            {' '}<strong style={{ color: theme.colors.plum, fontWeight: 600 }}>"use"</strong> link to copy that amount into your plan.
            Orange means you went over your previous plan; green means you came in under.
          </div>
        </aside>
      )}

      {/* Add/Edit dialogs */}
      {activeForm && (
        <FormSheet
          form={activeForm}
          budget={budget}
          categories={categories}
          parentCategories={userParents}
          debtBucket={debtBucket}
          goalBucket={goalBucket}
          viewMonth={month}
          viewYear={year}
          onClose={() => setActiveForm(null)}
          onSaved={async () => { setActiveForm(null); await fetchAll() }}
        />
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────

function PageHeader({ month, year, partnerProfile, onPrev, onNext, readOnly, locked, isMobile }) {
  const sharingFirst = partnerProfile ? firstName(partnerProfile.full_name) : null
  const today = new Date()
  const isCurrent = month === today.getMonth() + 1 && year === today.getFullYear()
  return (
    <div style={isMobile ? styles.headerMobile : styles.header}>
      <div style={styles.headerLeft}>
        <h1 style={styles.pageTitle}>Plan</h1>
        <div style={atoms.pageMeta}>
          <span>Give every dollar a job</span>
          {sharingFirst && (
            <>
              <span style={atoms.pageMetaDot} />
              <span style={atoms.sharingPill}>
                Sharing with {sharingFirst}
              </span>
            </>
          )}
          {readOnly && (
            <>
              <span style={atoms.pageMetaDot} />
              <span style={{ color: theme.colors.inkMuted, fontWeight: 500 }}>Read-only (past month)</span>
            </>
          )}
          {locked && !readOnly && (
            <>
              <span style={atoms.pageMetaDot} />
              <span style={styles.lockedPill}>Locked</span>
            </>
          )}
        </div>
      </div>
      <div style={styles.monthNav}>
        <button
          type="button"
          className="btn-compact"
          style={styles.monthNavBtn}
          onClick={onPrev}
          aria-label="Previous month"
        >
          <span aria-hidden="true" style={{ fontSize: '17px', lineHeight: 1 }}>‹</span>
        </button>
        <span style={styles.currentMonth}>
          {SHORT_MONTHS[month - 1]} {year}
          {isCurrent && <span style={styles.currentBadge}>now</span>}
        </span>
        <button
          type="button"
          className="btn-compact"
          style={styles.monthNavBtn}
          onClick={onNext}
          aria-label="Next month"
        >
          <span aria-hidden="true" style={{ fontSize: '17px', lineHeight: 1 }}>›</span>
        </button>
      </div>
    </div>
  )
}

function Ledger({ expectedIncome, allocated, leftToAssign, balanced, overAllocated, isMobile, isPastMonth, monthSpent }) {
  // Past-month branch: show "Income earned / Spent / Net" — no forward-looking math.
  if (isPastMonth) {
    const net = expectedIncome - monthSpent
    const netColor = net >= 0 ? theme.colors.green : theme.colors.rose
    const netMessage = expectedIncome === 0 && monthSpent === 0
      ? 'No data recorded for this month'
      : net >= 0
        ? `${formatMoney(net)} saved this month`
        : `${formatMoney(Math.abs(net))} overspent this month`
    return (
      <div style={isMobile ? styles.ledgerMobile : styles.ledger}>
        <LedgerCell label="Income earned" amount={expectedIncome} />
        <LedgerCell label="Spent" amount={monthSpent} />
        <LedgerCell
          label={net >= 0 ? 'Net (saved)' : 'Net (overspent)'}
          amount={Math.abs(net)}
          sign={net < 0 ? '−' : ''}
          color={netColor}
          message={netMessage}
          target
        />
      </div>
    )
  }

  // Current/future month: original forward-looking ledger
  const targetMessage = balanced
    ? 'Every dollar has a job!'
    : overAllocated
      ? `Allocated ${formatMoney(Math.abs(leftToAssign))} more than your income`
      : 'Keep going — every dollar needs a job'
  const targetColor = balanced
    ? theme.colors.green
    : overAllocated
      ? theme.colors.rose
      : theme.colors.orange

  return (
    <div style={isMobile ? styles.ledgerMobile : styles.ledger}>
      <LedgerCell label="Expected income" amount={expectedIncome} />
      <LedgerCell label="Allocated" amount={allocated} />
      <LedgerCell
        label="Left to assign"
        amount={Math.abs(leftToAssign)}
        sign={leftToAssign < 0 ? '−' : ''}
        color={targetColor}
        message={targetMessage}
        target
      />
    </div>
  )
}

function LedgerCell({ label, amount, sign = '', color, message, target = false }) {
  const cur = getCurrentCurrency()
  const parts = splitMoney(amount)
  return (
    <div style={{
      ...styles.ledgerCell,
      ...(target ? styles.ledgerCellTarget : {}),
    }}>
      <div style={{
        ...styles.ledgerLabel,
        color: target && color ? color : theme.colors.inkMuted,
      }}>
        {label}
      </div>
      <div style={{
        ...styles.ledgerAmount,
        color: target && color ? color : theme.colors.plum,
      }}>
        <span style={{
          ...styles.ledgerCurrency,
          color: target && color ? color : theme.colors.plumSoft,
        }}>
          {sign}{cur.symbol}
        </span>
        {parts.whole}
        <span style={{
          ...styles.ledgerCents,
          color: target && color ? color : theme.colors.plumSoft,
        }}>
          .{parts.cents}
        </span>
      </div>
      {message && (
        <div style={{ ...styles.targetMessage, color }}>{message}</div>
      )}
    </div>
  )
}

function FlowBar({ segments, isMobile }) {
  return (
    <>
      <div style={isMobile ? styles.flowBarMobile : styles.flowBar}>
        {segments.map(seg => (
          <div
            key={seg.kind}
            style={{
              width: `${seg.pct}%`,
              height: '100%',
              background: flowColor(seg.kind),
              borderRight: seg.kind === 'left' ? 'none' : `1px solid ${theme.colors.cream}`,
            }}
            title={`${flowLabel(seg.kind)}: ${formatMoney(seg.amount)}`}
          />
        ))}
      </div>
      <div style={isMobile ? styles.flowLegendMobile : styles.flowLegend}>
        {segments.map(seg => (
          <span key={seg.kind} style={styles.flowLegendItem}>
            <span style={{ ...styles.flowLegendSwatch, background: flowColor(seg.kind) }} />
            {flowLabel(seg.kind)}{' '}
            <strong style={{ color: theme.colors.plum, fontWeight: 600 }}>
              {formatMoney(seg.amount)}
            </strong>
          </span>
        ))}
      </div>
    </>
  )
}

function flowColor(kind) {
  switch (kind) {
    case 'bills': return theme.colors.plumSoft
    case 'debts': return theme.colors.orange
    case 'goals': return theme.colors.purple
    case 'cats':  return theme.colors.green
    case 'left':  return theme.colors.cream3
    default:      return theme.colors.inkQuiet
  }
}
function flowLabel(kind) {
  switch (kind) {
    case 'bills': return 'Bills'
    case 'debts': return 'Debts'
    case 'goals': return 'Goals'
    case 'cats':  return 'Categories'
    case 'left':  return 'Unassigned'
    default:      return kind
  }
}

function Section({ iconKind, title, subtotalLabel, subtotalAmount, actionLabel, onAction, secondaryActionLabel, onSecondaryAction, children }) {
  return (
    <section style={styles.section}>
      <div style={styles.sectionHead}>
        <div style={styles.sectionLeft}>
          <h2 style={styles.sectionTitle}>{title}</h2>
          <span style={styles.sectionSubtotal}>
            {subtotalLabel} <strong style={{ color: theme.colors.plum, fontWeight: 600 }}>{formatMoney(subtotalAmount)}</strong>
          </span>
        </div>
        <div style={styles.sectionActions}>
          {secondaryActionLabel && (
            <button
              type="button"
              className="btn-compact"
              style={styles.sectionAction}
              onClick={onSecondaryAction}
            >
              {secondaryActionLabel}
            </button>
          )}
          {actionLabel && (
            <button
              type="button"
              className="btn-compact"
              style={styles.sectionAction}
              onClick={onAction}
            >
              {actionLabel}
            </button>
          )}
        </div>
      </div>
      <div style={styles.card}>
        {children}
      </div>
    </section>
  )
}

function sectionIconBg(kind) {
  switch (kind) {
    case 'income': return theme.colors.greenSoft
    case 'bills':  return 'rgba(68, 53, 96, 0.10)'
    case 'debts':  return theme.colors.orangeSoft
    case 'goals':  return theme.colors.purpleSoft
    case 'cats':   return theme.colors.greenSoft
    default:       return theme.colors.cream2
  }
}
function sectionIconColor(kind) {
  switch (kind) {
    case 'income': return theme.colors.green
    case 'bills':  return theme.colors.plumSoft
    case 'debts':  return theme.colors.orange
    case 'goals':  return theme.colors.purple
    case 'cats':   return theme.colors.green
    default:       return theme.colors.plumSoft
  }
}

// ─────────────────────────────────────────────────────────────
// Lines
// ─────────────────────────────────────────────────────────────

function LineShell({ label, meta, summary, history, viewYear, viewMonth, readOnly, value, onChange, isIncome = false, onUseChip, actions, isMobile, expandable = false }) {
  // Common shell for income/bill/debt/goal/subcategory rows.
  // When `expandable` is true, the long-form `meta` lives in a collapsed
  // panel that toggles with a chevron. The short-form `summary` (or a
  // shortened version of meta) shows in the always-visible row.
  const [expanded, setExpanded] = useState(false)
  const collapsedMeta = expandable ? (summary || '') : meta
  const showExpanded = expandable && expanded && meta && meta !== summary
  return (
    <div style={isMobile ? styles.lineMobile : styles.line}>
      <div style={styles.lineLabel}>
        <span style={styles.lineName}>{label}</span>
        {collapsedMeta && <span style={styles.lineMeta}>{collapsedMeta}</span>}
        {showExpanded && (
          <div style={styles.lineExpandedMeta}>{meta}</div>
        )}
      </div>
      {!isMobile && (
        <HistoryStrip
          history={history}
          viewYear={viewYear}
          viewMonth={viewMonth}
          onUseChip={onUseChip}
          readOnly={readOnly}
        />
      )}
      <div style={styles.lineRight}>
        {expandable && meta && meta !== summary && (
          <button
            type="button"
            className="btn-compact"
            style={{
              ...styles.lineExpandBtn,
              transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            }}
            onClick={() => setExpanded(e => !e)}
            aria-label={expanded ? 'Collapse details' : 'Expand details'}
            aria-expanded={expanded}
          >
            <span aria-hidden="true" style={{ fontSize: '14px', lineHeight: 1 }}>›</span>
          </button>
        )}
        <AmountInput
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          isIncome={isIncome}
        />
        {actions}
      </div>
      {isMobile && (
        <div style={styles.historyMobile}>
          <HistoryStrip
            history={history}
            viewYear={viewYear}
            viewMonth={viewMonth}
            onUseChip={onUseChip}
            readOnly={readOnly}
          />
        </div>
      )}
    </div>
  )
}

function IncomeLine({ source, history, viewYear, viewMonth, readOnly, saving, onSave, onEdit }) {
  const ctx = useOutletContext() || {}
  const isMobile = !!ctx.isMobile
  const meta = `${frequencyLabel(source.frequency)}${source.is_variable ? '' : ''}`
  const monthly = monthlyEquivalentForUI(source)
  const tag = source.is_variable
    ? <span style={styles.incomeTag}>VARIABLE</span>
    : null
  return (
    <LineShell
      label={<>{source.name}{tag}</>}
      meta={`${meta}${source.frequency !== 'monthly' ? ` → ${formatMoney(monthly)}/mo` : ''}`}
      history={history}
      viewYear={viewYear}
      viewMonth={viewMonth}
      readOnly={readOnly}
      value={source.amount}
      onChange={onSave}
      isIncome
      isMobile={isMobile}
      actions={onEdit && (
        <RowActions onEdit={onEdit} saving={saving} />
      )}
    />
  )
}

function DebtLine({ debt, line, history, viewYear, viewMonth, readOnly, saving, onSave, onEdit, onDelete }) {
  const ctx = useOutletContext() || {}
  const isMobile = !!ctx.isMobile
  // Summary: short, useful at-a-glance info
  const summaryParts = []
  if (debt.current_balance) summaryParts.push(`${formatMoney(debt.current_balance)} balance`)
  if (!line) summaryParts.push('not in this month\'s plan')
  const summary = summaryParts.join(' · ')
  // Full meta: includes APR and any other detail
  const fullParts = []
  if (debt.current_balance) fullParts.push(`${formatMoney(debt.current_balance)} balance`)
  if (debt.interest_rate)   fullParts.push(`${debt.interest_rate}% APR`)
  if (debt.type)            fullParts.push(`${debt.type.replace(/_/g, ' ')}`)
  if (!line) fullParts.push('not in this month\'s plan')
  const fullMeta = fullParts.join(' · ')
  return (
    <LineShell
      label={debt.name}
      summary={summary}
      meta={fullMeta}
      expandable
      history={history}
      viewYear={viewYear}
      viewMonth={viewMonth}
      readOnly={readOnly}
      value={line ? line.limit_amount : debt.monthly_payment}
      onChange={onSave}
      isMobile={isMobile}
      actions={(onEdit || onDelete) && (
        <RowActions onEdit={onEdit} onDelete={onDelete} saving={saving} />
      )}
    />
  )
}

function GoalLine({ goal, line, history, viewYear, viewMonth, readOnly, saving, onSave, onEdit, onDelete }) {
  const ctx = useOutletContext() || {}
  const isMobile = !!ctx.isMobile
  const target = parseFloat(goal.target_amount || 0)
  const current = parseFloat(goal.current_amount || 0)
  const pct = target > 0 ? Math.min((current / target) * 100, 100) : 0
  const targetDateStr = goal.target_date
    ? new Date(goal.target_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    : null
  const meta = `${formatMoney(current)} of ${formatMoney(target)} saved · ${pct.toFixed(0)}%${targetDateStr ? ` · target ${targetDateStr}` : ''}`
  return (
    <LineShell
      label={goal.name}
      meta={meta}
      history={history}
      viewYear={viewYear}
      viewMonth={viewMonth}
      readOnly={readOnly}
      value={line ? line.limit_amount : 0}
      onChange={onSave}
      isMobile={isMobile}
      actions={(onEdit || onDelete) && (
        <RowActions onEdit={onEdit} onDelete={onDelete} saving={saving} />
      )}
    />
  )
}

function CategoryGroup({
  parent, subs, bills = [], rollup, history, isExpanded, onToggle, viewYear, viewMonth,
  readOnly, savingId, history_byCat,
  onSubSave, onParentSave, onAddSub, onEditCat, onDeleteCat,
  onBillSave, onEditBill, onDeleteBill, onAddBill,
}) {
  const ctx = useOutletContext() || {}
  const isMobile = !!ctx.isMobile
  const hasSubs = subs.length > 0
  const hasBills = bills.length > 0
  const hasContent = hasSubs || hasBills
  const billSum = bills.reduce((s, b) => s + parseFloat(b.amount || 0), 0)
  const subMeta = hasSubs
    ? `${subs.length} subcategor${subs.length === 1 ? 'y' : 'ies'}`
    : 'leaf category'
  const metaText = hasBills ? `${subMeta} · ${formatMoney(billSum)} in bills` : subMeta

  return (
    <div>
      <div style={isMobile ? styles.catLineMobile : styles.catLine}>
        {hasContent ? (
          <button
            type="button"
            className="btn-compact"
            style={{
              ...styles.catToggle,
              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            }}
            onClick={onToggle}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            <span aria-hidden="true" style={{ fontSize: '14px', lineHeight: 1 }}>›</span>
          </button>
        ) : (
          <span style={{ width: '24px' }} />
        )}
        <div style={styles.lineLabel}>
          <span style={styles.lineName}>{parent.name}</span>
          <span style={styles.lineMeta}>{metaText}</span>
        </div>
        {!isMobile && (
          <HistoryStrip
            history={history}
            viewYear={viewYear}
            viewMonth={viewMonth}
            onUseChip={hasSubs ? null : (amount => onParentSave(amount))}
            readOnly={readOnly || hasSubs}
          />
        )}
        <div style={styles.lineRight}>
          {hasSubs ? (
            <div style={styles.catRollup}>
              {formatMoney(rollup)}
              <span style={styles.catRollupLabel}>computed</span>
            </div>
          ) : (
            <AmountInput
              value={parent.limit_amount}
              onChange={onParentSave}
              readOnly={readOnly}
            />
          )}
          {!readOnly && (
            <RowActions
              onEdit={() => onEditCat(parent)}
              onDelete={() => onDeleteCat(parent)}
              saving={savingId === parent.id}
            />
          )}
        </div>
        {isMobile && (
          <div style={styles.historyMobile}>
            <HistoryStrip
              history={history}
              viewYear={viewYear}
              viewMonth={viewMonth}
              onUseChip={hasSubs ? null : (amount => onParentSave(amount))}
              readOnly={readOnly || hasSubs}
            />
          </div>
        )}
      </div>

      {/* Subcategories + inline bills */}
      {isExpanded && hasContent && (
        <div style={styles.subcatWrap}>
          {subs.map(sub => {
            // Try UUID first (current budget); fall back to canonical name
            // (cross-budget — e.g. May's view loading historical actuals
            // from January where the same "Rent" sub had a different UUID).
            const parentName = (parent.name || '').toLowerCase().trim()
            const ownName = (sub.name || '').toLowerCase().trim()
            const subHistory =
              (history_byCat[`category:${sub.id}`] && Object.keys(history_byCat[`category:${sub.id}`]).length > 0)
                ? history_byCat[`category:${sub.id}`]
                : (history_byCat[`cat:${parentName}::${ownName}`] || {})
            return (
              <div key={sub.id} style={isMobile ? styles.subLineMobile : styles.subLine}>
                <div style={styles.subBullet}>
                  <span style={{ color: theme.colors.inkQuiet, fontSize: '13px', lineHeight: 1 }} aria-hidden="true">↳</span>
                </div>
                <div style={styles.lineLabel}>
                  <span style={styles.subName}>{sub.name}</span>
                </div>
                {!isMobile && (
                  <HistoryStrip
                    history={subHistory}
                    viewYear={viewYear}
                    viewMonth={viewMonth}
                    onUseChip={amount => onSubSave(sub, amount)}
                    readOnly={readOnly}
                  />
                )}
                <div style={styles.lineRight}>
                  <AmountInput
                    value={sub.limit_amount}
                    onChange={(amount) => onSubSave(sub, amount)}
                    readOnly={readOnly}
                    compact
                  />
                  {!readOnly && (
                    <RowActions
                      onEdit={() => onEditCat(sub)}
                      onDelete={() => onDeleteCat(sub)}
                      saving={savingId === sub.id}
                    />
                  )}
                </div>
                {isMobile && (
                  <div style={styles.historyMobile}>
                    <HistoryStrip
                      history={subHistory}
                      viewYear={viewYear}
                      viewMonth={viewMonth}
                      onUseChip={amount => onSubSave(sub, amount)}
                      readOnly={readOnly}
                    />
                  </div>
                )}
              </div>
            )
          })}
          {bills.map(bill => (
            <BillSubRow
              key={bill.id}
              bill={bill}
              readOnly={readOnly}
              saving={savingId === bill.id}
              onSave={(amount) => onBillSave(bill, amount)}
              onEdit={() => onEditBill(bill)}
              onDelete={() => onDeleteBill(bill)}
            />
          ))}
          {!readOnly && (
            <div style={styles.subcatAddRow}>
              <button
                type="button"
                className="btn-compact"
                style={styles.subcatAdd}
                onClick={onAddSub}
              >
                Add subcategory
              </button>
              <button
                type="button"
                className="btn-compact"
                style={styles.subcatAdd}
                onClick={onAddBill}
              >
                + Add bill
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Bill rendered as a subcategory-style row inside its parent category.
function BillSubRow({ bill, readOnly, saving, onSave, onEdit, onDelete }) {
  const ctx = useOutletContext() || {}
  const isMobile = !!ctx.isMobile
  const freq = bill.frequency && bill.frequency !== 'monthly'
    ? (bill.frequency === 'one_time' ? 'One time' : bill.frequency.charAt(0).toUpperCase() + bill.frequency.slice(1))
    : ''
  const due = `Due ${bill.due_day}${ordinalSuffix(bill.due_day)}${freq ? ` · ${freq}` : ''}`
  return (
    <div style={isMobile ? styles.subLineMobile : styles.subLine}>
      <div style={styles.subBullet}>
        <span style={{ color: theme.colors.inkQuiet, fontSize: '13px', lineHeight: 1 }} aria-hidden="true">↳</span>
      </div>
      <div style={styles.lineLabel}>
        <span style={styles.subName}>
          {bill.name}<span style={styles.billTag}>Bill</span>
        </span>
        <span style={styles.lineMeta}>{due}</span>
      </div>
      {!isMobile && <div aria-hidden="true" />}
      <div style={styles.lineRight}>
        <AmountInput value={bill.amount} onChange={onSave} readOnly={readOnly} compact />
        {!readOnly && <RowActions onEdit={onEdit} onDelete={onDelete} saving={saving} />}
      </div>
    </div>
  )
}

// Unassigned bills — shown above the categories with an inline assign-a-category
// picker. Selecting a subcategory writes bills.category_id immediately.
function UnassignedBills({ bills, subOptions, readOnly, savingId, onAssign, onBillSave, onEditBill, onDeleteBill }) {
  return (
    <div style={styles.unassignedGroup}>
      <div style={styles.unassignedHead}>
        Unassigned bills · {bills.length}
        <span style={styles.unassignedHint}>Assign each to a category to complete your plan</span>
      </div>
      {bills.map(bill => {
        const freq = bill.frequency && bill.frequency !== 'monthly'
          ? (bill.frequency === 'one_time' ? 'One time' : bill.frequency.charAt(0).toUpperCase() + bill.frequency.slice(1))
          : ''
        const due = `Due ${bill.due_day}${ordinalSuffix(bill.due_day)}${freq ? ` · ${freq}` : ''}`
        return (
          <div key={bill.id} style={styles.unassignedRow}>
            <div style={styles.unassignedLabel}>
              <span style={styles.subName}>
                {bill.name}<span style={styles.billTag}>Bill</span>
              </span>
              <span style={styles.lineMeta}>{due}</span>
            </div>
            <select
              style={styles.assignSelect}
              value=""
              disabled={readOnly}
              onChange={(e) => onAssign(bill, e.target.value)}
              aria-label={`Assign a category to ${bill.name}`}
            >
              <option value="">← Assign a category</option>
              {subOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
            <div style={styles.unassignedRight}>
              <AmountInput value={bill.amount} onChange={(amt) => onBillSave(bill, amt)} readOnly={readOnly} compact />
              {!readOnly && (
                <RowActions onEdit={() => onEditBill(bill)} onDelete={() => onDeleteBill(bill)} saving={savingId === bill.id} />
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// Bits & pieces
// ─────────────────────────────────────────────────────────────

function HistoryStrip({ history, viewYear, viewMonth, onUseChip, readOnly }) {
  // History keys: "YYYY-M" (1-based month). We render 3 prior months.
  const months = []
  for (let i = 3; i >= 1; i--) {
    const d = new Date(viewYear, viewMonth - 1 - i, 1)
    const ym = `${d.getFullYear()}-${d.getMonth() + 1}`
    const isLatest = i === 1
    months.push({
      key: ym,
      label: SHORT_MONTHS[d.getMonth()],
      amount: history[ym] || null,
      isLatest,
    })
  }

  if (months.every(m => m.amount === null || m.amount === 0)) {
    return <div style={styles.history}><span style={styles.chipEmpty}>no history</span></div>
  }

  return (
    <div style={styles.history}>
      {months.map(m => {
        if (m.amount === null) {
          return (
            <div key={m.key} style={styles.chipEmptyDot}>
              <span style={styles.chipMonth}>{m.label}</span>
              <span style={{ ...styles.chipAmount, color: theme.colors.inkQuiet }}>—</span>
            </div>
          )
        }
        const clickable = m.isLatest && !readOnly && onUseChip
        return (
          <button
            key={m.key}
            type="button"
            className="btn-compact"
            style={{
              ...styles.chip,
              ...(m.isLatest ? styles.chipLatest : {}),
              cursor: clickable ? 'pointer' : 'default',
            }}
            onClick={clickable ? () => onUseChip(m.amount) : undefined}
            disabled={!clickable}
            title={clickable ? 'Use this amount' : null}
          >
            <span style={styles.chipMonth}>
              {m.label}{m.isLatest && clickable ? ' · use' : ''}
            </span>
            <span style={styles.chipAmount}>{formatMoney(m.amount)}</span>
          </button>
        )
      })}
    </div>
  )
}

function AmountInput({ value, onChange, readOnly, isIncome = false, compact = false }) {
  // Save-on-blur model: the input is fully editable while focused; we only
  // commit (which triggers a fetchAll up the tree) when the user blurs or
  // hits Enter. This avoids the mid-typing remount that was kicking the
  // user out of the field as they typed the last digit.
  const [local, setLocal] = useState(formatInput(value))
  const lastValueRef = useRef(value)
  const isFocusedRef = useRef(false)

  // Keep local state in sync when parent value changes — but ONLY when
  // the input isn't focused. Otherwise an in-flight render from somewhere
  // else (event bus, sibling save, etc.) could clobber what the user is
  // typing.
  useEffect(() => {
    if (isFocusedRef.current) return
    if (parseFloat(value || 0) !== parseFloat(lastValueRef.current || 0)) {
      setLocal(formatInput(value))
      lastValueRef.current = value
    }
  }, [value])

  const commit = () => {
    const stripped = (local || '').toString().replace(/[$,\s]/g, '')
    const num = parseFloat(stripped)
    if (isNaN(num)) {
      // Reset to last good value on invalid input
      setLocal(formatInput(lastValueRef.current))
      return
    }
    setLocal(formatInput(num))
    if (Math.abs(num - parseFloat(lastValueRef.current || 0)) >= 0.005) {
      lastValueRef.current = num
      onChange(num)
    }
  }

  const handleChange = (e) => {
    setLocal(e.target.value)
  }

  const handleFocus = () => {
    isFocusedRef.current = true
  }

  const handleBlur = () => {
    isFocusedRef.current = false
    commit()
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.target.blur() // triggers commit via handleBlur
    } else if (e.key === 'Escape') {
      // Bail out — restore last saved value, lose focus
      setLocal(formatInput(lastValueRef.current))
      e.target.blur()
    }
  }

  return (
    <input
      type="text"
      inputMode="decimal"
      value={local}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      readOnly={readOnly}
      style={{
        ...styles.lineInput,
        ...(compact ? styles.lineInputCompact : {}),
        ...(isIncome ? { color: theme.colors.green } : {}),
        ...(readOnly ? { background: 'transparent', cursor: 'default', borderColor: 'transparent' } : {}),
      }}
    />
  )
}

function RowActions({ onEdit, onDelete, saving }) {
  return (
    <div style={styles.rowActions}>
      {saving && (
        <span style={styles.savingDot} title="Saving">…</span>
      )}
      {onEdit && (
        <button
          type="button"
          className="btn-compact"
          style={styles.rowActionBtn}
          onClick={onEdit}
          aria-label="Edit"
          title="Edit"
        >
          Edit
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          className="btn-compact"
          style={styles.rowActionBtn}
          onClick={onDelete}
          aria-label="Delete"
          title="Delete"
        >
          Delete
        </button>
      )}
    </div>
  )
}

function EmptyLine({ text, cta, onCtaClick }) {
  return (
    <div style={styles.emptyLine}>
      <span>{text}</span>
      {cta && (
        <button
          type="button"
          className="btn-compact"
          style={styles.emptyLineCta}
          onClick={onCtaClick}
        >
          {cta}
        </button>
      )}
    </div>
  )
}

function BalanceSummary({
  leftToAssign, balanced, overAllocated, uncategorizedBillCount = 0,
  locked = false, lockedAt, lockBusy = false, onLock, onUnlock,
  firstGoal, firstGoalLineId, onAutoAssign,
}) {
  // ─── Locked state — quiet, informational (v2 chunk 4) ────────
  // Once locked the plan reads as settled; the only affordance is unlocking.
  if (locked) {
    return (
      <div style={{
        ...styles.balanceSummary,
        background: theme.colors.cream2,
        borderColor: theme.colors.line,
      }}>
        <div style={styles.balanceText}>
          <span style={{ ...styles.balanceTitle, color: theme.colors.plumSoft }}>
            Plan locked
          </span>
          <span style={styles.balanceSub}>
            Locked {formatLockTime(lockedAt)}. Unlock to make changes.
          </span>
        </div>
        <button
          type="button"
          style={styles.balanceUnlockBtn}
          onClick={onUnlock}
          disabled={lockBusy}
        >
          {lockBusy ? 'Unlocking…' : 'Unlock to edit'}
        </button>
      </div>
    )
  }

  const billsGate = uncategorizedBillCount > 0
  const isPositive = leftToAssign > 0.5
  const isOver = overAllocated
  const stateColors = billsGate
    ? { bg: 'linear-gradient(135deg, #FEF3E2 0%, #FFF8EE 100%)', border: 'rgba(217, 119, 6, 0.18)', titleColor: theme.colors.orange }
    : balanced
      ? { bg: 'linear-gradient(135deg, #DCF5E4 0%, #F0FBF3 100%)', border: 'rgba(22, 163, 74, 0.18)', titleColor: theme.colors.green }
      : isOver
        ? { bg: 'linear-gradient(135deg, #FCE7F3 0%, #FFF1F8 100%)', border: 'rgba(190, 24, 93, 0.18)', titleColor: theme.colors.rose }
        : { bg: 'linear-gradient(135deg, #FEF3E2 0%, #FFF8EE 100%)', border: 'rgba(217, 119, 6, 0.18)', titleColor: theme.colors.orange }

  const title = billsGate
    ? `Assign ${uncategorizedBillCount} bill${uncategorizedBillCount === 1 ? '' : 's'} to ${uncategorizedBillCount === 1 ? 'a category' : 'categories'} first`
    : balanced
      ? '✓ Balanced — every dollar has a job'
      : isOver
        ? `${formatMoney(Math.abs(leftToAssign))} over-allocated`
        : `${formatMoney(leftToAssign)} still to assign`
  const sub = billsGate
    ? 'Every bill needs a category before you can lock in. Use the picker in the Unassigned bills group above.'
    : balanced
      ? 'Nice work. Lock it in to settle this month’s plan.'
      : isOver
        ? 'You’ve allocated more than your expected income. Reduce somewhere or note that this month draws on savings.'
        : 'Move to savings, pay down more debt, or expand a category — every dollar should have a job.'

  // Lock-in button: active only when balanced AND every bill is categorized.
  // Otherwise it's disabled and its label states exactly what's blocking.
  const canLock = balanced && !billsGate
  const lockLabel = billsGate
    ? `Assign ${uncategorizedBillCount} bill${uncategorizedBillCount === 1 ? '' : 's'} first`
    : balanced
      ? (lockBusy ? 'Locking in…' : 'Lock in this plan')
      : isOver
        ? `Over by ${formatMoney(Math.abs(leftToAssign))} — pull from a category`
        : 'Keep going — every dollar needs a job'

  const canAutoAssign = isPositive && !billsGate && firstGoal && firstGoalLineId

  return (
    <div style={{
      ...styles.balanceSummary,
      background: stateColors.bg,
      borderColor: stateColors.border,
    }}>
      <div style={styles.balanceText}>
        <span style={{ ...styles.balanceTitle, color: stateColors.titleColor }}>
          {title}
        </span>
        <span style={styles.balanceSub}>{sub}</span>
      </div>
      <div style={styles.balanceActions}>
        {canAutoAssign && (
          <button
            type="button"
            style={styles.balanceAction}
            onClick={onAutoAssign}
          >
            Auto-assign to {firstGoal.name}
          </button>
        )}
        <button
          type="button"
          style={canLock ? styles.balanceLockBtn : styles.balanceLockBtnDisabled}
          onClick={canLock ? onLock : undefined}
          disabled={!canLock || lockBusy}
        >
          {lockLabel}
        </button>
      </div>
    </div>
  )
}

// Format a locked_at timestamp into a short, friendly stamp for the footer/header.
function formatLockTime(ts) {
  if (!ts) return ''
  try {
    return new Date(ts).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    })
  } catch { return '' }
}

function RailRow({ label, value }) {
  return (
    <div style={styles.railRow}>
      <span style={styles.railRowLabel}>{label}</span>
      <span style={styles.railRowValue}>{value}</span>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// FormSheet — covers all add/edit dialogs
// ─────────────────────────────────────────────────────────────

function FormSheet({ form, budget, categories, parentCategories, debtBucket, goalBucket, viewMonth, viewYear, onClose, onSaved }) {
  const isEdit = typeof form === 'object' && form.type && form.type.endsWith('-edit')
  // Resolve form into a known kind for the sub-form switch:
  let kind = typeof form === 'string' ? form : form.type
  if (kind === 'income-edit') kind = 'income'
  if (kind === 'bill-edit')   kind = 'bill'
  if (kind === 'debt-edit')   kind = 'debt'
  if (kind === 'goal-edit')   kind = 'goal'
  if (kind === 'category-edit') kind = 'category'

  const titleMap = {
    income:      isEdit ? 'Edit income' : 'Add income',
    bill:        isEdit ? 'Edit bill' : 'Add bill',
    debt:        isEdit ? 'Edit debt' : 'Add debt',
    goal:        isEdit ? 'Edit savings goal' : 'Add savings goal',
    category:    isEdit ? 'Edit category' : 'Add category',
    subcategory: 'Add subcategory',
  }

  return (
    <div
      style={styles.formOverlay}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      role="dialog"
      aria-modal="true"
    >
      <div style={styles.formSheet}>
        <header style={styles.formHeader}>
          <span style={styles.formTitle}>{titleMap[kind] || 'Edit'}</span>
          <button
            type="button"
            className="btn-compact"
            style={styles.formClose}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div style={styles.formBody}>
          {kind === 'income' && (
            <IncomeForm
              source={isEdit ? form.source : null}
              viewMonth={viewMonth}
              viewYear={viewYear}
              onSaved={onSaved}
            />
          )}
          {kind === 'bill' && (
            <BillForm
              bill={isEdit ? form.bill : null}
              presetCategoryId={!isEdit && typeof form === 'object' ? form.presetCategoryId : null}
              onSaved={onSaved}
            />
          )}
          {kind === 'debt' && (
            <DebtForm
              debt={isEdit ? form.debt : null}
              budget={budget}
              debtBucket={debtBucket}
              onSaved={onSaved}
            />
          )}
          {kind === 'goal' && (
            <GoalForm
              goal={isEdit ? form.goal : null}
              budget={budget}
              goalBucket={goalBucket}
              onSaved={onSaved}
            />
          )}
          {(kind === 'category' || kind === 'subcategory') && (
            <CategoryForm
              cat={isEdit ? form.cat : null}
              parentId={kind === 'subcategory' ? form.parentId : null}
              parentCategories={parentCategories}
              budget={budget}
              categories={categories}
              onSaved={onSaved}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Income form ──────────────────────────────────────────────
function IncomeForm({ source, viewMonth, viewYear, onSaved }) {
  const isEdit = !!source
  const [name, setName] = useState(source?.name || '')
  const [amount, setAmount] = useState(source?.amount?.toString() || '')
  const [frequency, setFrequency] = useState(source?.frequency || 'monthly')
  const [type, setType] = useState(source?.source_type || 'salary')
  const [isVariable, setIsVariable] = useState(source?.is_variable || false)
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    if (!name || !amount) return
    setSaving(true)
    const { data: { user } } = await supabase.auth.getUser()
    if (isEdit) {
      await supabase.from('income_sources').update({
        name, amount: parseFloat(amount),
        frequency, source_type: type, is_variable: isVariable,
      }).eq('id', source.id)
      emitDataEvent(EVENTS.INCOME_UPDATED)
    } else {
      await supabase.from('income_sources').insert({
        user_id: user.id, name, amount: parseFloat(amount),
        frequency, source_type: type, is_variable: isVariable,
        is_active: true, month: viewMonth, year: viewYear,
      })
      emitDataEvent(EVENTS.INCOME_SAVED)
    }
    setSaving(false)
    onSaved()
  }

  const handleDelete = async () => {
    if (!isEdit) return
    if (!window.confirm(`Remove "${source.name}"?`)) return
    await supabase.from('income_sources').update({ is_active: false }).eq('id', source.id)
    emitDataEvent(EVENTS.INCOME_DELETED)
    onSaved()
  }

  return (
    <>
      <FormField label="Name" required>
        <input style={styles.formInput} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Salary, Freelance" />
      </FormField>
      <FormField label="Type">
        <select style={styles.formInput} value={type} onChange={(e) => setType(e.target.value)}>
          {INCOME_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </FormField>
      <FormField label="Amount" required>
        <input style={styles.formInput} type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
      </FormField>
      <FormField label="Frequency">
        <select style={styles.formInput} value={frequency} onChange={(e) => setFrequency(e.target.value)}>
          {INCOME_FREQUENCY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </FormField>
      <FormToggle
        label="Variable income"
        hint="Commission, freelance, irregular payments"
        on={isVariable}
        onChange={setIsVariable}
      />
      <FormFooter
        onSave={handleSave}
        onDelete={isEdit ? handleDelete : null}
        saving={saving}
        canSave={!!name && !!amount}
        saveLabel={isEdit ? 'Save changes' : 'Add income'}
      />
    </>
  )
}

// ─── Bill form ────────────────────────────────────────────────
function BillForm({ bill, onSaved, presetCategoryId = null }) {
  const { partnerId } = usePartner()
  const isEdit = !!bill
  const [name, setName] = useState(bill?.name || '')
  const [amount, setAmount] = useState(bill?.amount?.toString() || '')
  const [dueDay, setDueDay] = useState(bill?.due_day?.toString() || '1')
  const [frequency, setFrequency] = useState(bill?.frequency || 'monthly')
  const [reminderDays, setReminderDays] = useState(bill?.reminder_days?.toString() || '3')
  const [endDate, setEndDate] = useState(bill?.end_date || '')
  const [categoryId, setCategoryId] = useState(bill?.category_id || presetCategoryId || '')
  const [allCategories, setAllCategories] = useState([])
  // "Pays from" account (Chunk 7 — Money Movement). Optional; null = advice-only.
  const [accountId, setAccountId] = useState(bill?.account_id || '')
  const [accounts, setAccounts] = useState([])
  const [notes, setNotes] = useState(bill?.notes || '')
  const [receiptUrl, setReceiptUrl] = useState(bill?.receipt_url || null)
  const [saving, setSaving] = useState(false)
  const [showScanner, setShowScanner] = useState(false)

  const isOneTime = frequency === 'one_time'

  // Load categories for the bill's category picker — pulls all categories
  // across all budgets and dedupes by name+parent (since UUIDs differ per
  // monthly budget).
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return
      const userIds = [user.id, ...(partnerId ? [partnerId] : [])]
      const [catsRes, acctsRes] = await Promise.all([
        supabase.from('budget_categories')
          .select('id, name, parent_id, budget_id')
          .eq('user_id', user.id),
        supabase.from('accounts')
          .select('id, name')
          .in('user_id', userIds)
          .order('name'),
      ])
      if (cancelled) return
      setAllCategories(catsRes.data || [])
      setAccounts(acctsRes.data || [])
    })()
    return () => { cancelled = true }
  }, [partnerId])

  // Receipt scanner hands back a draft bill via onDataExtracted (Bills.js
  // legacy callback shape that ReceiptScanner still supports for mode='bill').
  const handleScanned = (data, file, scannedReceiptUrl) => {
    // Scanner returns: { name, amount, due_date, notes, frequency }, file, receiptUrl
    if (data.name) setName(data.name)
    if (data.amount) setAmount(data.amount.toString())
    if (data.due_date) {
      const d = new Date(data.due_date + 'T12:00:00')
      if (!isNaN(d.getTime())) {
        setDueDay(d.getDate().toString())
        // For one-time bills, also capture full date
        if (frequency === 'one_time') {
          setEndDate(data.due_date)
        }
      }
    }
    if (data.notes) setNotes(data.notes)
    if (scannedReceiptUrl) setReceiptUrl(scannedReceiptUrl)
    setShowScanner(false)
  }

  const handleSave = async () => {
    if (!name || !amount || !dueDay) return
    setSaving(true)
    const { data: { user } } = await supabase.auth.getUser()
    const data = {
      name,
      amount: parseFloat(amount),
      due_day: parseInt(dueDay, 10),
      frequency,
      reminder_days: parseInt(reminderDays, 10) || 3,
      is_recurring: !isOneTime,
      end_date: isOneTime ? null : (endDate || null),
      notes: notes || null,
      receipt_url: receiptUrl || null,
      category_id: categoryId || null,
      account_id: accountId || null,
      is_active: true,
    }
    if (isEdit) {
      await supabase.from('bills').update(data).eq('id', bill.id)
      emitDataEvent(EVENTS.BILL_UPDATED)
    } else {
      await supabase.from('bills').insert({ ...data, user_id: user.id })
      emitDataEvent(EVENTS.BILL_SAVED)
    }
    setSaving(false)
    onSaved()
  }

  const handleDelete = async () => {
    if (!isEdit) return
    if (!window.confirm(`Remove "${bill.name}"?`)) return
    await supabase.from('bills').update({ is_active: false }).eq('id', bill.id)
    emitDataEvent(EVENTS.BILL_DELETED)
    onSaved()
  }
  if (showScanner) {
    return (
      <ReceiptScanner
        mode="bill"
        onClose={() => setShowScanner(false)}
        onDataExtracted={handleScanned}
      />
    )
  }

  return (
    <>
      {!isEdit && (
        <button
          type="button"
          className="btn-compact"
          style={styles.scanCta}
          onClick={() => setShowScanner(true)}
        >
          Scan a receipt to fill this in
        </button>
      )}
      <FormField label="Name" required>
        <input style={styles.formInput} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Rent, Internet" />
      </FormField>
      <FormField label="Amount" required>
        <input style={styles.formInput} type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
      </FormField>
      <FormField label="Frequency">
        <select style={styles.formInput} value={frequency} onChange={(e) => {
          const newFreq = e.target.value
          setFrequency(newFreq)
          // When switching to one_time, commit the reconstructed date into
          // endDate so the form is immediately valid (instead of showing a
          // displayed date that's not actually saved in state).
          if (newFreq === 'one_time' && !endDate && dueDay) {
            const now = new Date()
            const m = (now.getMonth() + 1).toString().padStart(2, '0')
            const d = parseInt(dueDay, 10).toString().padStart(2, '0')
            setEndDate(`${now.getFullYear()}-${m}-${d}`)
          }
        }}>
          {BILL_FREQUENCY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </FormField>
      {isOneTime ? (
        <FormField label="Date" required>
          <input
            style={styles.formInput}
            type="date"
            value={endDate || (() => {
              // Date was lost when frequency changed — reconstruct from dueDay
              // using current month/year so the user doesn't lose what they
              // typed (or what the scanner extracted).
              if (!dueDay) return ''
              const now = new Date()
              const m = (now.getMonth() + 1).toString().padStart(2, '0')
              const d = parseInt(dueDay, 10).toString().padStart(2, '0')
              return `${now.getFullYear()}-${m}-${d}`
            })()}
            onChange={(e) => {
              setEndDate(e.target.value)
              // Sync due_day from the picked date for compatibility
              if (e.target.value) {
                const d = new Date(e.target.value + 'T12:00:00')
                if (!isNaN(d.getTime())) setDueDay(d.getDate().toString())
              }
            }}
          />
        </FormField>
      ) : (
        <>
          <FormField label="Due day of month" required>
            <input style={styles.formInput} type="number" min="1" max="31" value={dueDay} onChange={(e) => setDueDay(e.target.value)} />
          </FormField>
          <FormField label="End date (optional)">
            <input style={styles.formInput} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </FormField>
        </>
      )}

      <FormField label="Category">
        <select
          style={styles.formInput}
          value={(() => {
            // Resolve the saved category_id to whichever option is in our dedup'd list
            if (!categoryId) return ''
            const rowCat = allCategories.find(c => c.id === categoryId)
            if (!rowCat) return ''
            const parentNameById = {}
            allCategories.forEach(c => {
              if (c.parent_id === null) parentNameById[c.id] = (c.name || '').toLowerCase().trim()
            })
            const rowParentName = rowCat.parent_id ? (parentNameById[rowCat.parent_id] || '') : ''
            const rowName = (rowCat.name || '').toLowerCase().trim()
            const seen = new Set()
            const sorted = allCategories.filter(c => c.parent_id !== null).slice().sort((a, b) => a.name.localeCompare(b.name))
            for (const c of sorted) {
              const pn = parentNameById[c.parent_id] || ''
              const key = `${pn}::${(c.name || '').toLowerCase().trim()}`
              if (seen.has(key)) continue
              seen.add(key)
              if (pn === rowParentName && (c.name || '').toLowerCase().trim() === rowName) {
                return c.id
              }
            }
            return ''
          })()}
          onChange={(e) => setCategoryId(e.target.value || null)}
        >
          <option value="">— Pick a category (optional) —</option>
          {(() => {
            const seen = new Set()
            const parentNameById = {}
            allCategories.forEach(c => {
              if (c.parent_id === null) parentNameById[c.id] = (c.name || '').toLowerCase().trim()
            })
            return allCategories
              .filter(cat => cat.parent_id !== null)
              .filter(cat => {
                const parentName = parentNameById[cat.parent_id] || ''
                const key = `${parentName}::${(cat.name || '').toLowerCase().trim()}`
                if (seen.has(key)) return false
                seen.add(key)
                return true
              })
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))
          })()}
        </select>
      </FormField>
      <FormField label="Pays from">
        <select
          style={styles.formInput}
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
        >
          <option value="">None (no specific account)</option>
          {accounts.map(a => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
      </FormField>
      <FormField label="Reminder days before due">
        <input style={styles.formInput} type="number" min="0" max="14" value={reminderDays} onChange={(e) => setReminderDays(e.target.value)} />
      </FormField>
      <FormField label="Notes (optional)">
        <input style={styles.formInput} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. account number, reference" />
      </FormField>
      <FormFooter
        onSave={handleSave}
        onDelete={isEdit ? handleDelete : null}
        saving={saving}
        canSave={!!name && !!amount && (isOneTime ? !!endDate : !!dueDay)}
        saveLabel={isEdit ? 'Save changes' : 'Add bill'}
      />
    </>
  )
}

// ─── Debt form ────────────────────────────────────────────────
function DebtForm({ debt, budget, debtBucket, onSaved }) {
  const isEdit = !!debt
  const [name, setName] = useState(debt?.name || '')
  const [type, setType] = useState(debt?.type || 'credit_card')
  const [currentBalance, setCurrentBalance] = useState(debt?.current_balance?.toString() || '')
  const [monthlyPayment, setMonthlyPayment] = useState(debt?.monthly_payment?.toString() || '')
  const [interestRate, setInterestRate] = useState(debt?.interest_rate?.toString() || '')
  const [saving, setSaving] = useState(false)
const handleSave = async () => {
    if (!name || !currentBalance) return
    setSaving(true)
    const { data: { user } } = await supabase.auth.getUser()
    const data = {
      name, type,
      current_balance: parseFloat(currentBalance),
      monthly_payment: monthlyPayment ? parseFloat(monthlyPayment) : null,
      interest_rate: interestRate ? parseFloat(interestRate) : null,
      is_simple_mode: true,
      updated_at: new Date().toISOString(),
    }
    if (isEdit) {
      await supabase.from('debts').update(data).eq('id', debt.id)
      emitDataEvent(EVENTS.DEBT_UPDATED)
    } else {
      const { data: created } = await supabase.from('debts').insert({ ...data, user_id: user.id }).select().single()
      emitDataEvent(EVENTS.DEBT_SAVED)
      // Auto-create budget category for this month, mirroring Debts.js's existing pattern
      if (created && monthlyPayment && budget) {
        let parent = debtBucket
        if (!parent) {
          const { data: newParent } = await supabase.from('budget_categories').insert({
            user_id: user.id, budget_id: budget.id, name: DEBT_BUCKET, icon: '💳',
            limit_amount: 0, sort_order: 999,
          }).select().single()
          parent = newParent
        }
        if (parent) {
          await supabase.from('budget_categories').insert({
            user_id: user.id, budget_id: budget.id,
            name, icon: '💳',
            limit_amount: parseFloat(monthlyPayment),
            parent_id: parent.id, sort_order: 999,
          })
          emitDataEvent(EVENTS.CATEGORY_SAVED)
        }
      }
    }
    setSaving(false)
    onSaved()
  }

  const handleDelete = async () => {
    if (!isEdit) return
    if (!window.confirm(`Delete "${debt.name}"?`)) return
    await supabase.from('debts').delete().eq('id', debt.id)
    emitDataEvent(EVENTS.DEBT_DELETED)
    // Note: we leave the budget_category around as historical record
    onSaved()
  }

  const debtTypes = [
    { value: 'credit_card',  label: '💳 Credit Card' },
    { value: 'student_loan', label: '🎓 Student Loan' },
    { value: 'auto_loan',    label: '🚗 Auto Loan' },
    { value: 'mortgage',     label: '🏠 Mortgage' },
    { value: 'personal_loan',label: '💵 Personal Loan' },
    { value: 'medical',      label: '🏥 Medical' },
    { value: 'other',        label: '📋 Other' },
  ]

  return (
    <>
      <FormField label="Name" required>
        <input style={styles.formInput} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Chase Sapphire" />
      </FormField>
      <FormField label="Type">
        <select style={styles.formInput} value={type} onChange={(e) => setType(e.target.value)}>
          {debtTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </FormField>
      <FormField label="Current balance" required>
        <input style={styles.formInput} type="number" step="0.01" value={currentBalance} onChange={(e) => setCurrentBalance(e.target.value)} />
      </FormField>
      <FormField label="Monthly payment">
        <input style={styles.formInput} type="number" step="0.01" value={monthlyPayment} onChange={(e) => setMonthlyPayment(e.target.value)} />
      </FormField>
      <FormField label="Interest rate (APR %)">
        <input style={styles.formInput} type="number" step="0.01" value={interestRate} onChange={(e) => setInterestRate(e.target.value)} placeholder="e.g. 18.99" />
      </FormField>
      <FormFooter
        onSave={handleSave}
        onDelete={isEdit ? handleDelete : null}
        saving={saving}
        canSave={!!name && !!currentBalance}
        saveLabel={isEdit ? 'Save changes' : 'Add debt'}
      />
    </>
  )
}

// ─── Goal form ────────────────────────────────────────────────
function GoalForm({ goal, budget, goalBucket, onSaved }) {
  const isEdit = !!goal
  const [name, setName] = useState(goal?.name || '')
  const [targetAmount, setTargetAmount] = useState(goal?.target_amount?.toString() || '')
  const [currentAmount, setCurrentAmount] = useState(goal?.current_amount?.toString() || '0')
  const [targetDate, setTargetDate] = useState(goal?.target_date || '')
  const [monthlyContribution, setMonthlyContribution] = useState('')
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    if (!name || !targetAmount) return
    setSaving(true)
    const { data: { user } } = await supabase.auth.getUser()
    const data = {
      name,
      target_amount: parseFloat(targetAmount),
      current_amount: parseFloat(currentAmount || 0),
      target_date: targetDate || null,
    }
    if (isEdit) {
      await supabase.from('savings_goals').update(data).eq('id', goal.id)
      emitDataEvent(EVENTS.GOAL_UPDATED)
    } else {
      const { data: created } = await supabase.from('savings_goals').insert({ ...data, user_id: user.id }).select().single()
      emitDataEvent(EVENTS.GOAL_SAVED)
      // Auto-create a goal budget category for this month
      if (created && budget) {
        let parent = goalBucket
        if (!parent) {
          const { data: newParent } = await supabase.from('budget_categories').insert({
            user_id: user.id, budget_id: budget.id, name: GOAL_BUCKET, icon: '🎯',
            limit_amount: 0, sort_order: 998,
          }).select().single()
          parent = newParent
        }
        if (parent) {
          await supabase.from('budget_categories').insert({
            user_id: user.id, budget_id: budget.id,
            name, icon: '🎯',
            limit_amount: monthlyContribution ? parseFloat(monthlyContribution) : 0,
            parent_id: parent.id, sort_order: 998,
          })
          emitDataEvent(EVENTS.CATEGORY_SAVED)
        }
      }
    }
    setSaving(false)
    onSaved()
  }

  const handleDelete = async () => {
    if (!isEdit) return
    if (!window.confirm(`Delete "${goal.name}"?`)) return
    await supabase.from('savings_goals').delete().eq('id', goal.id)
    emitDataEvent(EVENTS.GOAL_DELETED)
    onSaved()
  }

  return (
    <>
      <FormField label="Name" required>
        <input style={styles.formInput} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Emergency Fund, Hawaii" />
      </FormField>
      <FormField label="Target amount" required>
        <input style={styles.formInput} type="number" step="0.01" value={targetAmount} onChange={(e) => setTargetAmount(e.target.value)} />
      </FormField>
      <FormField label="Already saved">
        <input style={styles.formInput} type="number" step="0.01" value={currentAmount} onChange={(e) => setCurrentAmount(e.target.value)} />
      </FormField>
      <FormField label="Target date (optional)">
        <input style={styles.formInput} type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
      </FormField>
      {!isEdit && (
        <FormField label="Monthly contribution (optional)">
          <input style={styles.formInput} type="number" step="0.01" value={monthlyContribution} onChange={(e) => setMonthlyContribution(e.target.value)} placeholder="0.00" />
        </FormField>
      )}
      <FormFooter
        onSave={handleSave}
        onDelete={isEdit ? handleDelete : null}
        saving={saving}
        canSave={!!name && !!targetAmount}
        saveLabel={isEdit ? 'Save changes' : 'Add goal'}
      />
    </>
  )
}

// ─── Category form ────────────────────────────────────────────
function CategoryForm({ cat, parentId, parentCategories, budget, categories, onSaved }) {
  const isEdit = !!cat
  const [name, setName] = useState(cat?.name || '')
  const [limit, setLimit] = useState(cat?.limit_amount?.toString() || '')
  const [type, setType] = useState(parentId ? 'subcategory' : (cat?.parent_id ? 'subcategory' : 'category'))
  const [chosenParent, setChosenParent] = useState(parentId || cat?.parent_id || '')
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    if (!name) return
    if (type === 'subcategory' && !chosenParent) return
    setSaving(true)
    const { data: { user } } = await supabase.auth.getUser()
    const data = {
      name,
      limit_amount: parseFloat(limit) || 0,
      parent_id: type === 'subcategory' ? chosenParent : null,
    }
    if (isEdit) {
      await supabase.from('budget_categories').update(data).eq('id', cat.id)
      emitDataEvent(EVENTS.CATEGORY_UPDATED)
    } else {
      const maxSort = categories.reduce((m, c) => Math.max(m, c.sort_order || 0), 0)
      await supabase.from('budget_categories').insert({
        ...data,
        user_id: user.id,
        budget_id: budget.id,
        sort_order: maxSort + 1,
      })
      emitDataEvent(EVENTS.CATEGORY_SAVED)
    }
    setSaving(false)
    onSaved()
  }

  return (
    <>
      {!isEdit && !parentId && (
        <FormField label="Type">
          <div style={styles.formSegment}>
            <button
              type="button"
              className="btn-compact"
              style={{ ...styles.formSegOption, ...(type === 'category' ? styles.formSegOptionActive : {}) }}
              onClick={() => { setType('category'); setChosenParent('') }}
            >
              Category
            </button>
            <button
              type="button"
              className="btn-compact"
              style={{ ...styles.formSegOption, ...(type === 'subcategory' ? styles.formSegOptionActive : {}) }}
              onClick={() => setType('subcategory')}
            >
              Subcategory
            </button>
          </div>
        </FormField>
      )}
      {type === 'subcategory' && !parentId && (
        <FormField label="Parent category" required>
          <select style={styles.formInput} value={chosenParent} onChange={(e) => setChosenParent(e.target.value)}>
            <option value="">Select parent…</option>
            {parentCategories.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </FormField>
      )}
      <FormField label="Name" required>
        <input style={styles.formInput} value={name} onChange={(e) => setName(e.target.value)} placeholder={type === 'subcategory' ? 'e.g. Groceries' : 'e.g. Food'} />
      </FormField>
      <FormField label="Monthly limit">
        <input style={styles.formInput} type="number" step="0.01" value={limit} onChange={(e) => setLimit(e.target.value)} placeholder="0.00" />
      </FormField>
      <FormFooter
        onSave={handleSave}
        saving={saving}
        canSave={!!name && (type === 'category' || !!chosenParent)}
        saveLabel={isEdit ? 'Save changes' : `Add ${type}`}
      />
    </>
  )
}

// ─── Form primitives ──────────────────────────────────────────
function FormField({ label, required, children }) {
  return (
    <label style={styles.formField}>
      <span style={styles.formLabel}>
        {label}{required && <span style={{ color: theme.colors.rose, marginLeft: '3px' }}>*</span>}
      </span>
      {children}
    </label>
  )
}

function FormToggle({ label, hint, on, onChange }) {
  return (
    <div style={styles.formToggleRow}>
      <div>
        <div style={styles.formToggleLabel}>{label}</div>
        <div style={styles.formToggleHint}>{hint}</div>
      </div>
      <button
        type="button"
        className="btn-compact"
        style={{ ...styles.formToggle, background: on ? theme.colors.green : theme.colors.cream3 }}
        onClick={() => onChange(!on)}
        aria-pressed={on}
      >
        <div style={{
          ...styles.formToggleThumb,
          left: on ? '22px' : '2px',
        }} />
      </button>
    </div>
  )
}

function FormFooter({ onSave, onDelete, saving, canSave, saveLabel }) {
  return (
    <div style={styles.formFooter}>
      {onDelete && (
        <button
          type="button"
          style={styles.formDelete}
          onClick={onDelete}
        >
          Delete
        </button>
      )}
      <button
        type="button"
        style={{ ...styles.formSave, opacity: !canSave || saving ? 0.5 : 1, cursor: !canSave || saving ? 'not-allowed' : 'pointer' }}
        onClick={onSave}
        disabled={!canSave || saving}
      >
        {saving ? 'Saving…' : saveLabel}
      </button>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

function splitMoney(amount) {
  const cur = getCurrentCurrency()
  const num = Math.abs(parseFloat(amount || 0))
  const formatter = new Intl.NumberFormat(cur.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const formatted = formatter.format(num)
  // Find decimal separator (comma in some locales, dot in others)
  const parts = formatter.formatToParts(num)
  const dec = parts.find(p => p.type === 'decimal')?.value || '.'
  const [w, c] = formatted.split(dec)
  return { whole: w, cents: c || '00' }
}

function formatMoney(amount) {
  const cur = getCurrentCurrency()
  const num = parseFloat(amount || 0)
  return new Intl.NumberFormat(cur.locale, {
    style: 'currency',
    currency: cur.code,
    maximumFractionDigits: 0,
  }).format(num)
}

function formatInput(value) {
  const cur = getCurrentCurrency()
  const num = parseFloat(value || 0)
  if (isNaN(num)) return ''
  return new Intl.NumberFormat(cur.locale, {
    style: 'currency',
    currency: cur.code,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num)
}

function frequencyLabel(frequency) {
  return FREQUENCY_OPTIONS.find(f => f.value === frequency)?.label || 'Monthly'
}

function ordinalSuffix(n) {
  const num = parseInt(n, 10)
  const j = num % 10, k = num % 100
  if (j === 1 && k !== 11) return 'st'
  if (j === 2 && k !== 12) return 'nd'
  if (j === 3 && k !== 13) return 'rd'
  return 'th'
}

function monthlyEquivalentForUI(source) {
  const a = parseFloat(source.amount || 0)
  switch (source.frequency) {
    case 'weekly':       return a * 4.33
    case 'biweekly':     return a * 2.17
    case 'semi_monthly': return a * 2
    case 'quarterly':    return a / 3
    case 'annual':       return a / 12
    case 'one_time':     return a
    default:             return a
  }
}

// ─────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────

const c = theme.colors
const f = theme.fonts
const styles = {
  // Shell
  shell: {
    display: 'grid',
    gridTemplateColumns: '1fr 280px',
    minHeight: '100%',
    background: c.cream,
    fontFamily: f.body,
    color: c.plum,
  },
  shellMobile: {
    background: c.cream,
    fontFamily: f.body,
    color: c.plum,
    paddingBottom: '40px',
  },
  main: {
    padding: '24px 32px 80px',
    maxWidth: '900px',
    margin: '0 auto',
    width: '100%',
  },
  mainMobile: {
    padding: '0 16px 80px',
  },

  loading: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '10px', padding: '60px', color: c.inkMuted, fontSize: '14px',
  },

  // Page header
  header: {
    display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
    marginBottom: '20px', gap: '14px', flexWrap: 'wrap',
  },
  headerMobile: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '20px 0 18px', gap: '12px', flexWrap: 'wrap',
  },
  headerLeft: { display: 'flex', flexDirection: 'column', gap: '6px' },
  pageTitle: {
    fontFamily: f.display, fontWeight: 600, fontSize: '32px',
    letterSpacing: '-0.025em', color: c.plum, margin: 0,
  },
  monthNav: {
    display: 'flex', alignItems: 'center', gap: '8px',
    background: c.white, border: `1px solid ${c.line}`,
    borderRadius: '12px', padding: '4px',
  },
  monthNavBtn: {
    background: 'transparent', border: 'none', width: '32px', height: '32px',
    borderRadius: '8px', display: 'grid', placeItems: 'center',
    cursor: 'pointer', color: c.plumSoft,
  },
  currentMonth: {
    fontFamily: f.body, fontSize: '14px', fontWeight: 600, color: c.plum,
    minWidth: '90px', textAlign: 'center', display: 'flex',
    alignItems: 'center', justifyContent: 'center', gap: '6px',
  },
  currentBadge: {
    fontSize: '10px', background: c.greenSoft, color: c.green,
    padding: '2px 6px', borderRadius: '6px', fontWeight: 600,
    letterSpacing: '0.04em', textTransform: 'uppercase',
  },
  lockedPill: {
    fontSize: '10px', background: c.cream3, color: c.plumSoft,
    padding: '2px 8px', borderRadius: '6px', fontWeight: 600,
    letterSpacing: '0.04em', textTransform: 'uppercase',
  },

  // Ledger
  ledger: {
    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
    background: c.white, border: `1px solid ${c.line}`,
    borderRadius: '14px', overflow: 'hidden', marginBottom: '14px',
    boxShadow: theme.shadows.card,
  },
  ledgerMobile: {
    display: 'grid', gridTemplateColumns: '1fr',
    background: c.white, border: `1px solid ${c.line}`,
    borderRadius: '14px', overflow: 'hidden', marginBottom: '14px',
    boxShadow: theme.shadows.card,
  },
  ledgerCell: {
    padding: '18px 20px', borderRight: `1px solid ${c.line}`,
    display: 'flex', flexDirection: 'column', gap: '6px',
  },
  ledgerCellTarget: {
    background: c.cream2, borderRight: 'none',
  },
  ledgerLabel: {
    fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em',
    textTransform: 'uppercase', color: c.inkMuted,
  },
  ledgerAmount: {
    fontFamily: f.display, fontWeight: 600, fontSize: '36px',
    lineHeight: 1, letterSpacing: '-0.03em', color: c.plum,
    display: 'flex', alignItems: 'baseline', gap: '3px',
  },
  ledgerCurrency: {
    fontSize: '18px', fontWeight: 500, color: c.plumSoft,
    transform: 'translateY(-10px)',
  },
  ledgerCents: {
    fontSize: '20px', fontWeight: 500, color: c.plumSoft,
    transform: 'translateY(-8px)',
  },
  targetMessage: {
    fontSize: '12px', fontWeight: 500, marginTop: '4px',
  },

  // Flow bar
  flowBar: {
    display: 'flex', height: '8px', borderRadius: '999px',
    overflow: 'hidden', marginBottom: '8px',
    background: c.cream3,
  },
  flowBarMobile: {
    display: 'flex', height: '8px', borderRadius: '999px',
    overflow: 'hidden', marginBottom: '8px',
    background: c.cream3,
  },
  flowLegend: {
    display: 'flex', flexWrap: 'wrap', gap: '14px',
    padding: '4px 2px 18px', fontSize: '12.5px', color: c.inkMuted,
  },
  flowLegendMobile: {
    display: 'flex', flexWrap: 'wrap', gap: '10px',
    padding: '4px 0 14px', fontSize: '11.5px', color: c.inkMuted,
  },
  flowLegendItem: {
    display: 'inline-flex', alignItems: 'center', gap: '5px',
  },
  flowLegendSwatch: {
    width: '8px', height: '8px', borderRadius: '2px', flexShrink: 0,
  },

  // Plan body
  planBody: { display: 'flex', flexDirection: 'column', gap: '0' },

  // Section
  section: { marginBottom: '20px' },
  sectionHead: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 4px', marginBottom: '10px', gap: '10px', flexWrap: 'wrap',
  },
  sectionLeft: {
    display: 'flex', alignItems: 'center', gap: '10px',
    flexWrap: 'wrap',
  },
  sectionIcon: {
    width: '26px', height: '26px', borderRadius: '8px',
    display: 'grid', placeItems: 'center', flexShrink: 0,
  },
  sectionTitle: {
    fontFamily: f.body, fontSize: '15px', fontWeight: 600,
    color: c.plum, letterSpacing: '-0.005em', margin: 0,
  },
  sectionSubtotal: {
    fontSize: '12.5px', color: c.inkMuted,
  },
  sectionAction: {
    background: 'transparent', border: 'none', color: c.green,
    fontFamily: f.body, fontSize: '13px', fontWeight: 600,
    cursor: 'pointer', padding: '6px 8px', borderRadius: '6px',
    flexShrink: 0,
  },

  // Card (line container)
  card: {
    background: c.white, border: `1px solid ${c.line}`,
    borderRadius: '14px', overflow: 'hidden',
    boxShadow: theme.shadows.card,
  },

  // Line
  line: {
    display: 'grid',
    // Amount + row actions share the trailing column. (Was '160px 56px' with a
    // dead 56px column; merged to one 220px column so the amount has room for
    // "$X,XXX.XX" alongside the text Edit/Delete buttons — see Chunk 2d.)
    gridTemplateColumns: '1fr 280px 220px',
    gap: '14px', alignItems: 'center',
    padding: '14px 18px',
    borderBottom: `1px solid ${c.line}`,
    transition: 'background 100ms ease',
  },
  lineMobile: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: '8px', alignItems: 'center',
    padding: '14px 16px',
    borderBottom: `1px solid ${c.line}`,
  },
  lineLabel: {
    display: 'flex', flexDirection: 'column', gap: '2px',
    minWidth: 0,
  },
  lineName: {
    fontSize: '14.5px', fontWeight: 600, color: c.plum,
  },
  lineMeta: {
    fontSize: '12px', color: c.inkMuted,
    // Degrade gracefully when the label column is squeezed at narrow widths:
    // ellipsize on a single line instead of breaking words onto stacked lines.
    display: 'block', maxWidth: '100%',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  lineExpandedMeta: {
    fontSize: '12.5px', color: c.inkMuted,
    lineHeight: 1.5,
    marginTop: '6px',
    paddingTop: '8px',
    borderTop: `1px dashed ${c.line}`,
  },
  lineExpandBtn: {
    width: '20px', height: '20px',
    borderRadius: '6px',
    background: 'rgba(30, 16, 64, 0.04)',
    display: 'grid', placeItems: 'center',
    color: c.plumSoft,
    cursor: 'pointer',
    border: 'none',
    transition: 'transform 150ms ease, background 100ms ease',
    flexShrink: 0,
  },
  lineRight: {
    display: 'flex', alignItems: 'center', gap: '6px',
    minWidth: 0,
  },
  historyMobile: {
    gridColumn: '1 / -1',
    overflowX: 'auto',
  },

  // Income tag
  incomeTag: {
    display: 'inline-block',
    padding: '1px 7px',
    background: c.cream3,
    color: c.plumSoft,
    borderRadius: '999px',
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    marginLeft: '6px',
    verticalAlign: 'middle',
  },

  // History chips
  history: {
    display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
    gap: '6px', overflowX: 'auto',
  },
  chip: {
    display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
    gap: '1px', padding: '5px 10px',
    background: 'rgba(30, 16, 64, 0.04)',
    borderRadius: '8px', border: '1px solid transparent',
    transition: 'all 120ms ease', minWidth: '64px',
    fontFamily: f.body,
    flexShrink: 0,
  },
  chipLatest: {
    background: c.cream3,
    borderColor: c.lineStrong,
  },
  chipMonth: {
    fontSize: '10px', fontWeight: 600,
    color: c.inkMuted, letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  chipAmount: {
    fontFamily: f.display, fontWeight: 600,
    fontSize: '13px', color: c.plum,
    letterSpacing: '-0.01em',
  },
  chipEmpty: {
    fontSize: '11px', color: c.inkQuiet,
    fontStyle: 'italic', padding: '5px 8px',
  },
  chipEmptyDot: {
    display: 'inline-flex', flexDirection: 'column',
    alignItems: 'center', gap: '1px',
    padding: '5px 10px', minWidth: '64px',
    border: `1px dashed ${c.line}`, borderRadius: '8px',
    background: 'transparent',
  },

  // Line input
  lineInput: {
    background: c.cream2,
    border: '1px solid transparent',
    borderRadius: '10px',
    padding: '9px 12px',
    fontFamily: f.display,
    fontWeight: 600, fontSize: '15px',
    letterSpacing: '-0.015em', color: c.plum,
    width: '100%', textAlign: 'right',
    outline: 'none', transition: 'all 120ms ease',
    // Floor wide enough for "$X,XXX.XX" so the amount never clips to "$0.0"
    // when sharing the trailing column with the text Edit/Delete buttons.
    minWidth: '92px', maxWidth: '150px',
    boxSizing: 'border-box',
  },
  lineInputCompact: {
    fontSize: '13px', padding: '7px 10px',
    background: c.white, maxWidth: '110px',
  },

  // Category lines
  catLine: {
    display: 'grid',
    gridTemplateColumns: '32px 1fr 280px 220px',
    gap: '14px', alignItems: 'center',
    padding: '14px 18px',
    borderBottom: `1px solid ${c.line}`,
    transition: 'background 100ms ease',
  },
  catLineMobile: {
    display: 'grid',
    gridTemplateColumns: '32px 1fr auto',
    gap: '8px', alignItems: 'center',
    padding: '14px 16px',
    borderBottom: `1px solid ${c.line}`,
  },
  catToggle: {
    width: '24px', height: '24px',
    borderRadius: '6px',
    background: 'rgba(30, 16, 64, 0.04)',
    display: 'grid', placeItems: 'center',
    color: c.plumSoft, cursor: 'pointer',
    border: 'none', transition: 'transform 150ms ease',
  },
  catRollup: {
    fontFamily: f.display, fontWeight: 600,
    fontSize: '15px', letterSpacing: '-0.015em',
    color: c.plum, textAlign: 'right',
    padding: '8px 10px',
    display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px',
    flexShrink: 0,
  },
  catRollupLabel: {
    fontFamily: f.body,
    color: c.inkQuiet, fontWeight: 500, fontSize: '10px',
    letterSpacing: '0.06em', textTransform: 'uppercase',
  },

  // Subcategory rows
  subcatWrap: {
    background: c.cream2,
    borderBottom: `1px solid ${c.line}`,
  },
  subLine: {
    display: 'grid',
    gridTemplateColumns: '32px 1fr 280px 220px',
    gap: '14px', alignItems: 'center',
    padding: '11px 18px',
    borderBottom: '1px solid rgba(30, 16, 64, 0.05)',
  },
  subLineMobile: {
    display: 'grid',
    gridTemplateColumns: '24px 1fr auto',
    gap: '8px', alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid rgba(30, 16, 64, 0.05)',
  },
  subBullet: {
    display: 'flex', justifyContent: 'flex-end',
  },
  subName: {
    fontSize: '13.5px', fontWeight: 500, color: c.plumSoft,
  },
  subcatAdd: {
    width: '100%', padding: '11px 18px',
    background: c.cream2, color: c.green,
    fontFamily: f.body, fontSize: '13px', fontWeight: 600,
    cursor: 'pointer', border: 'none',
    display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
    gap: '6px', borderTop: `1px dashed ${c.line}`,
  },
  subcatAddRow: { display: 'flex' },

  // Bill-inside-category (Chunk 3)
  billTag: {
    display: 'inline-block', marginLeft: '7px', verticalAlign: 'middle',
    padding: '0 6px', background: c.cream3, color: c.plumSoft,
    borderRadius: '999px', fontSize: '9px', fontWeight: 700,
    letterSpacing: '0.06em', textTransform: 'uppercase',
  },
  unassignedGroup: {
    background: c.orangeSoft,
    borderBottom: `1px solid ${c.line}`,
  },
  unassignedHead: {
    display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap',
    padding: '12px 18px 8px',
    fontFamily: f.body, fontSize: '12.5px', fontWeight: 700,
    color: c.orange, letterSpacing: '0.02em', textTransform: 'uppercase',
  },
  unassignedHint: {
    fontWeight: 500, fontSize: '11.5px', color: c.inkMuted,
    textTransform: 'none', letterSpacing: 0,
  },
  unassignedRow: {
    display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
    padding: '10px 18px',
    borderTop: '1px solid rgba(217, 119, 6, 0.12)',
  },
  unassignedLabel: {
    display: 'flex', flexDirection: 'column', gap: '2px',
    minWidth: 0, flex: '1 1 150px',
  },
  unassignedRight: {
    display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto',
  },
  assignSelect: {
    flex: '0 1 210px',
    padding: '8px 10px', borderRadius: '8px',
    border: `1px solid ${c.orange}`,
    background: c.white, color: c.plum,
    fontFamily: f.body, fontSize: '13px', fontWeight: 600,
    cursor: 'pointer', outline: 'none',
  },
  sectionActions: {
    display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0,
  },

  // Row actions
  rowActions: {
    display: 'flex', alignItems: 'center', gap: '2px',
    flexShrink: 0,
  },
  rowActionBtn: {
    padding: '4px 8px',
    background: 'transparent', border: 'none',
    color: c.inkQuiet, cursor: 'pointer',
    borderRadius: '6px', fontFamily: 'inherit',
    fontSize: '12.5px', fontWeight: 600,
    transition: 'background 120ms ease, color 120ms ease',
  },
  savingDot: {
    color: c.green, display: 'grid', placeItems: 'center',
    width: '20px', fontSize: '16px', lineHeight: 1,
  },

  // Empty line
  emptyLine: {
    padding: '18px 18px',
    color: c.inkMuted, fontSize: '13.5px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: '12px', flexWrap: 'wrap',
  },
  emptyLineCta: {
    background: 'transparent', color: c.green,
    border: 'none', fontFamily: f.body,
    fontSize: '13px', fontWeight: 600, cursor: 'pointer',
    padding: '4px 8px', borderRadius: '6px',
  },

  // Empty budget (no plan yet)
  emptyBudget: {
    background: c.white, border: `1px solid ${c.line}`,
    borderRadius: '16px', padding: '40px 28px',
    textAlign: 'center', maxWidth: '460px', margin: '40px auto',
    boxShadow: theme.shadows.card,
  },
  emptyBudgetIcon: {
    width: '56px', height: '56px', borderRadius: '16px',
    background: c.cream2, color: c.plumSoft,
    display: 'grid', placeItems: 'center',
    margin: '0 auto 16px',
    border: `1px solid ${c.line}`,
  },
  emptyBudgetTitle: {
    fontFamily: f.display, fontSize: '22px', fontWeight: 600,
    color: c.plum, letterSpacing: '-0.015em', margin: '0 0 10px',
  },
  emptyBudgetText: {
    fontSize: '14px', color: c.inkMuted, lineHeight: 1.55,
    margin: '0 0 22px',
  },
  emptyBudgetCta: {
    background: c.plum, color: c.cream,
    border: 'none', padding: '12px 24px',
    borderRadius: '10px', fontFamily: f.body,
    fontSize: '14px', fontWeight: 600, cursor: 'pointer',
  },

  // Past-month edit banner
  pastMonthBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
    marginTop: '12px',
    marginBottom: '4px',
    padding: '10px 14px',
    background: c.cream2,
    border: `1px solid ${c.line}`,
    borderRadius: '10px',
  },
  pastMonthBannerText: {
    fontSize: '12.5px',
    color: c.inkMuted,
    flex: 1,
    minWidth: '180px',
  },
  pastMonthBannerUnlockBtn: {
    background: c.plum,
    color: c.cream,
    border: 'none',
    padding: '7px 14px',
    borderRadius: '8px',
    fontFamily: f.body,
    fontSize: '12.5px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  pastMonthBannerLockBtn: {
    background: 'transparent',
    color: c.plum,
    border: `1px solid ${c.lineStrong}`,
    padding: '7px 14px',
    borderRadius: '8px',
    fontFamily: f.body,
    fontSize: '12.5px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  // Balance summary
  balanceSummary: {
    marginTop: '8px', padding: '20px 22px',
    border: '1px solid', borderRadius: '14px',
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', gap: '16px',
    flexWrap: 'wrap',
  },
  balanceText: {
    display: 'flex', flexDirection: 'column', gap: '4px',
    minWidth: 0, flex: 1,
  },
  balanceTitle: {
    fontFamily: f.display, fontWeight: 600,
    fontSize: '18px', letterSpacing: '-0.015em',
  },
  balanceSub: {
    fontSize: '13px', color: c.plumSoft,
  },
  balanceAction: {
    background: c.plum, color: c.cream,
    border: 'none', padding: '10px 16px',
    borderRadius: '10px', fontFamily: f.body,
    fontSize: '13px', fontWeight: 600, cursor: 'pointer',
    flexShrink: 0,
  },
  balanceActions: {
    display: 'flex', alignItems: 'center', gap: '10px',
    flexShrink: 0, flexWrap: 'wrap',
  },
  freedGoalNote: {
    display: 'flex', alignItems: 'flex-start', gap: '8px',
    padding: '10px 12px', margin: '2px 0 8px',
    background: c.greenSoft, borderRadius: '10px',
    fontSize: '13px', color: c.plumSoft, lineHeight: 1.4,
  },
  freedGoalDot: {
    width: '7px', height: '7px', borderRadius: '50%',
    background: c.green, marginTop: '5px', flexShrink: 0,
  },
  balanceLockBtn: {
    background: c.green, color: '#FFFFFF',
    border: 'none', padding: '11px 20px',
    borderRadius: '10px', fontFamily: f.body,
    fontSize: '14px', fontWeight: 600, cursor: 'pointer',
    flexShrink: 0,
  },
  balanceLockBtnDisabled: {
    background: c.cream3, color: c.inkMuted,
    border: 'none', padding: '11px 20px',
    borderRadius: '10px', fontFamily: f.body,
    fontSize: '14px', fontWeight: 600, cursor: 'not-allowed',
    flexShrink: 0,
  },
  balanceUnlockBtn: {
    background: 'transparent', color: c.plumSoft,
    border: `1px solid ${c.lineStrong}`, padding: '9px 16px',
    borderRadius: '10px', fontFamily: f.body,
    fontSize: '13px', fontWeight: 600, cursor: 'pointer',
    flexShrink: 0,
  },

  // Right rail
  rail: {
    background: c.cream2,
    borderLeft: `1px solid ${c.line}`,
    padding: '24px 22px',
    position: 'sticky', top: 0,
    height: '100vh', overflowY: 'auto',
  },
  railHead: { marginBottom: '16px' },
  railTitle: {
    fontFamily: f.display, fontWeight: 600,
    fontSize: '18px', letterSpacing: '-0.015em',
    color: c.plum, margin: 0,
  },
  railSub: {
    fontSize: '12.5px', color: c.inkMuted,
    lineHeight: 1.45, marginTop: '4px',
  },
  railMonth: {
    background: c.white, border: `1px solid ${c.line}`,
    borderRadius: '12px', padding: '14px 16px',
    marginBottom: '12px',
  },
  railMonthHead: {
    display: 'flex', alignItems: 'baseline',
    justifyContent: 'space-between', marginBottom: '8px',
  },
  railMonthName: {
    fontSize: '12.5px', fontWeight: 700,
    color: c.plum, letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  railMonthResult: {
    fontFamily: f.display, fontWeight: 600,
    fontSize: '13.5px', letterSpacing: '-0.01em',
  },
  railRow: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4px 0', fontSize: '12px',
  },
  railRowLabel: { color: c.plumSoft },
  railRowValue: { color: c.plum, fontWeight: 500 },
  railPlanned: { color: c.inkQuiet, fontWeight: 400 },
  railLink: {
    display: 'block', marginTop: '12px', textAlign: 'center',
    color: c.green, fontSize: '12.5px', fontWeight: 600,
    textDecoration: 'none', padding: '6px',
  },
  railDivider: {
    height: '1px', background: c.line, margin: '16px 0',
  },
  railTip: {
    background: 'rgba(139, 92, 246, 0.06)',
    border: '1px solid rgba(139, 92, 246, 0.15)',
    borderRadius: '10px', padding: '12px 14px',
    fontSize: '11.5px', color: c.plumSoft, lineHeight: 1.5,
  },

  // Form sheet
  formOverlay: {
    position: 'fixed', inset: 0, overscrollBehaviorX: 'contain',
    background: 'rgba(30, 16, 64, 0.6)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    zIndex: 800, fontFamily: f.body,
  },
  formSheet: {
    overscrollBehaviorX: 'contain',
    background: c.cream, borderRadius: '20px 20px 0 0',
    width: '100%', maxWidth: '500px',
    maxHeight: '92vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 -8px 40px rgba(30, 16, 64, 0.2)',
  },
  formHeader: {
    padding: '14px 20px 8px',
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${c.line}`, flexShrink: 0,
  },
  formTitle: {
    fontFamily: f.display, fontSize: '20px',
    fontWeight: 600, letterSpacing: '-0.02em', color: c.plum,
  },
  formClose: {
    width: '32px', height: '32px', borderRadius: '50%',
    background: 'rgba(30, 16, 64, 0.05)',
    display: 'grid', placeItems: 'center',
    cursor: 'pointer', color: c.plum, border: 'none',
    fontSize: '20px', lineHeight: 1,
  },
  formBody: {
    flex: 1, overflowY: 'auto',
    padding: '16px 22px calc(env(safe-area-inset-bottom, 16px) + 22px)',
  },
  formField: {
    display: 'flex', flexDirection: 'column', gap: '6px',
    marginBottom: '14px', fontFamily: f.body,
  },
  scanCta: {
    width: '100%',
    padding: '12px',
    marginBottom: '16px',
    background: c.greenSoft,
    color: c.green,
    border: `1px dashed rgba(22, 163, 74, 0.3)`,
    borderRadius: '12px',
    fontFamily: f.body, fontSize: '13.5px', fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: '8px',
  },
  formLabel: {
    fontSize: '12px', fontWeight: 600,
    color: c.inkMuted, letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  formInput: {
    width: '100%', padding: '11px 14px',
    background: c.white, border: `1px solid ${c.lineStrong}`,
    borderRadius: '10px', fontFamily: f.body,
    fontSize: '14.5px', color: c.plum, outline: 'none',
    transition: 'border-color 120ms ease',
    boxSizing: 'border-box',
  },
  iconPickerRow: {
    display: 'flex',
    alignItems: 'center',
  },
  iconPickerBtn: {
    background: c.cream2,
    border: `1px solid ${c.line}`,
    borderRadius: '10px',
    padding: '8px 14px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    minHeight: '46px',
    width: '100%',
    textAlign: 'left',
  },
  iconPickerGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(8, 1fr)',
    gap: '4px',
    marginTop: '8px',
    padding: '10px',
    background: c.cream3,
    borderRadius: '10px',
    maxHeight: '180px',
    overflowY: 'auto',
  },
  iconPickerOption: {
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: '8px',
    padding: '6px',
    fontSize: '20px',
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'all 100ms ease',
  },
  iconPickerOptionActive: {
    background: c.white,
    border: `1px solid ${c.green}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  formSegment: {
    display: 'grid', gridTemplateColumns: '1fr 1fr',
    background: c.cream3, borderRadius: '10px', padding: '4px',
    gap: '2px',
  },
  formSegOption: {
    padding: '9px', textAlign: 'center',
    fontSize: '13px', fontWeight: 600, color: c.inkMuted,
    borderRadius: '7px', cursor: 'pointer',
    border: 'none', background: 'transparent',
    fontFamily: f.body,
    transition: 'all 120ms ease',
  },
  formSegOptionActive: {
    background: c.white, color: c.plum,
    boxShadow: '0 1px 3px rgba(30, 16, 64, 0.1)',
  },
  formToggleRow: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 14px', background: c.white,
    border: `1px solid ${c.line}`,
    borderRadius: '10px', marginBottom: '14px', gap: '12px',
  },
  formToggleLabel: {
    fontSize: '14px', fontWeight: 600, color: c.plum,
  },
  formToggleHint: {
    fontSize: '12px', color: c.inkMuted, marginTop: '2px',
  },
  formToggle: {
    width: '44px', height: '24px', borderRadius: '12px',
    border: 'none', position: 'relative', cursor: 'pointer',
    transition: 'background 120ms ease', padding: 0,
    flexShrink: 0,
  },
  formToggleThumb: {
    width: '20px', height: '20px', borderRadius: '50%',
    background: c.white, position: 'absolute', top: '2px',
    transition: 'left 120ms ease',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  },
  formFooter: {
    display: 'flex', gap: '10px', marginTop: '8px',
  },
  formSave: {
    flex: 1, padding: '13px',
    background: c.plum, color: c.cream,
    border: 'none', borderRadius: '12px',
    fontFamily: f.body, fontSize: '14.5px', fontWeight: 600,
    cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  formDelete: {
    padding: '13px 18px',
    background: c.roseSoft, color: c.rose,
    border: `1px solid rgba(190, 24, 93, 0.2)`,
    borderRadius: '12px', fontFamily: f.body,
    fontSize: '13px', fontWeight: 600, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: '6px',
  },
}
